import { useState, useEffect, useRef } from 'react'
import { Copy, ChevronRight, Eye, EyeOff, ChevronDown, ArrowLeft, Menu, X, User, FileText, CheckSquare, Users, Settings, LayoutDashboard, Shield, Lock, Smartphone, Mail, Wallet, Trash2, Monitor, Activity, Gift, UserPlus, Filter, AlertTriangle, Check, Camera, ShieldCheck, ShieldOff, RefreshCw, LogOut } from 'lucide-react'
import { BicoinLogo } from '../../components/BicoinLogo'
import Layout from '../../components/Layout/Layout'
import { useAuth } from '../../context/AuthContext'
import { getProfile, changePassword, changeEmail, deleteAccountService, updateUserProfile, logoutUser, getNotifications, markNotificationRead, getReferralStats } from '../../services/userService'
import { getBalance, getSpotHoldings } from '../../services/walletService'
import { useToast } from '../../context/ToastContext'
import '../../styles/dashboard.css'
import { useNavigate, useLocation } from 'react-router-dom'

export default function ProfileOverview() {
    const navigate = useNavigate()
    const location = useLocation()
    const { logout, refreshUser } = useAuth()
    const { toast } = useToast()

    const handleLogout = () => {
        logoutUser()
        logout()
        navigate('/login')
    }
    const resolveInitialTab = (): 'overview' | 'verification' | 'security' | 'invite' | 'settings' => {
        const requested = new URLSearchParams(location.search).get('tab')
        // "referral" is an alias used by Home page shortcuts — the actual tab id is "invite"
        if (requested === 'referral') return 'invite'
        if (requested === 'overview' || requested === 'verification' || requested === 'security' || requested === 'invite' || requested === 'settings') {
            return requested
        }
        return 'overview'
    }
    const [activeAssetTab, setActiveAssetTab] = useState<'favorite' | 'hot' | 'gainers' | 'losers' | 'volume'>('favorite')
    const [activeProfileTab, setActiveProfileTab] = useState<'overview' | 'verification' | 'security' | 'invite' | 'settings'>(resolveInitialTab())
    const [showMobileMenu, setShowMobileMenu] = useState(false)
    const [profile, setProfile] = useState<any>(null)
    const [userBalance, setUserBalance] = useState(0)
    const [balanceLoading, setBalanceLoading] = useState(true)
    const [pageLoading, setPageLoading] = useState(true)
    const [profileLoaded, setProfileLoaded] = useState(false)
    const [balanceLoaded, setBalanceLoaded] = useState(false)
    const [balanceHidden, setBalanceHidden] = useState(false)
    const [marketData, setMarketData] = useState<Record<string, any>>({})

    // Orders state

    // Notifications state
    const [notifications, setNotifications] = useState<any[]>([])

    // Referral state
    const [referralStats, setReferralStats] = useState<any[]>([])

    // Security modal states
    const [secModal, setSecModal] = useState<'none' | 'password' | 'email' | 'phone' | 'delete' | 'device' | 'activity' | 'authenticator' | 'fund-password' | 'disable-2fa' | 'change-2fa'>('none')
    const [secLoading, setSecLoading] = useState(false)
    const [secError, setSecError] = useState('')
    const [secSuccess, setSecSuccess] = useState('')
    const [secForm, setSecForm] = useState<Record<string, string>>({})
    const [authStep, setAuthStep] = useState<1 | 2 | 3>(1)
    const [authSecret, setAuthSecret] = useState('')
    const generateNewSecret = () => {
        const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ234567'
        let s = ''
        for (let i = 0; i < 16; i++) s += chars[Math.floor(Math.random() * chars.length)]
        setAuthSecret(s)
    }

    const [deviceSessions, setDeviceSessions] = useState<any[]>([])

    useEffect(() => {
        generateNewSecret()
    }, [])

    const fetchDeviceSessions = async () => {
        try {
            const userInfo = localStorage.getItem('userInfo');
            const token = userInfo ? JSON.parse(userInfo).token : null;
            const res = await fetch(`${import.meta.env.VITE_API_BASE_URL || import.meta.env.VITE_API_URL}/api/users/devices`, {
                headers: { 'Authorization': `Bearer ${token}` }
            })
            if (res.ok) {
                const data = await res.json()
                setDeviceSessions(data)
            }
        } catch (e) {
            console.error('Error fetching device sessions', e)
        } finally {
        }
    }

    const handleRevokeDevice = async (sessionId: string) => {
        try {
            const userInfo = localStorage.getItem('userInfo');
            const token = userInfo ? JSON.parse(userInfo).token : null;
            const res = await fetch(`${import.meta.env.VITE_API_BASE_URL || import.meta.env.VITE_API_URL}/api/users/devices/revoke`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                },
                body: JSON.stringify({ sessionId })
            })
            if (res.ok) {
                toast('Device revoked successfully', 'success')
                fetchDeviceSessions()
            } else {
                const data = await res.json()
                toast(data.message || 'Failed to revoke device', 'error')
            }
        } catch (e) {
            toast('Error revoking device', 'error')
        }
    }

    useEffect(() => {
        if (secModal === 'device' || secModal === 'activity') {
            fetchDeviceSessions();
        }
    }, [secModal])

    const closeSecModal = () => { setSecModal('none'); setSecError(''); setSecSuccess(''); setSecForm({}); setAuthStep(1); }

    const compressAvatar = (file: File): Promise<string> => {
        return new Promise((resolve, reject) => {
            const img = new Image()
            const url = URL.createObjectURL(file)
            img.onload = () => {
                URL.revokeObjectURL(url)
                const SIZE = 200
                const canvas = document.createElement('canvas')
                canvas.width = SIZE
                canvas.height = SIZE
                const ctx = canvas.getContext('2d')
                if (!ctx) { reject(new Error('Canvas not supported')); return }
                // Center-crop: pick the largest square from center, then scale to 200x200
                const { width, height } = img
                const cropSize = Math.min(width, height)
                const sx = (width - cropSize) / 2
                const sy = (height - cropSize) / 2
                ctx.drawImage(img, sx, sy, cropSize, cropSize, 0, 0, SIZE, SIZE)
                // Compress iteratively until under 80KB
                let q = 0.85
                let base64 = canvas.toDataURL('image/jpeg', q)
                while (base64.length > 80 * 1024 && q > 0.1) {
                    q -= 0.1
                    base64 = canvas.toDataURL('image/jpeg', q)
                }
                resolve(base64)
            }
            img.onerror = () => { URL.revokeObjectURL(url); reject(new Error('Failed to load image')) }
            img.src = url
        })
    }

    // Avatar upload state
    const avatarInputRef = useRef<HTMLInputElement>(null)
    const [avatarPreview, setAvatarPreview] = useState<string | null>(() => {
        try {
            const stored = localStorage.getItem('userInfo')
            if (stored) {
                const parsed = JSON.parse(stored)
                return parsed.avatar || null
            }
        } catch { }
        return null
    })
    const [avatarUploading, setAvatarUploading] = useState(false)

    const handleAvatarClick = () => {
        avatarInputRef.current?.click()
    }

    const handleAvatarChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0]
        if (!file) return
        if (!file.type.startsWith('image/')) return
        if (file.size > 5 * 1024 * 1024) return // Max 5MB

        setAvatarUploading(true)
        try {
            const dataUrl = await compressAvatar(file)
            setAvatarPreview(dataUrl) // show compressed preview immediately
            const { getAuthHeaders } = await import('../../lib/authFetch')
            const apiBase = `${import.meta.env.VITE_API_BASE_URL || 'http://localhost:5000'}/api/users`
            const res = await fetch(`${apiBase}/avatar`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
                body: JSON.stringify({ image: dataUrl }),
            })
            const data = await res.json()
            if (data.success) {
                setAvatarPreview(data.data.avatarUrl)
                // Update stored userInfo so avatar persists across sessions
                const stored = localStorage.getItem('userInfo')
                if (stored) {
                    const parsed = JSON.parse(stored)
                    parsed.avatar = data.data.avatarUrl
                    localStorage.setItem('userInfo', JSON.stringify(parsed))
                    refreshUser()
                }
            }
        } catch {
            // keep local preview even if upload fails
        } finally {
            setAvatarUploading(false)
        }
        e.target.value = ''
    }

    // Load avatar from profile (Cloudinary URL) or fallback
    useEffect(() => {
        if (profile?.avatar) setAvatarPreview(profile.avatar)
    }, [profile])



    const handleDisable2FA = async () => {
        const code = secForm.otpCode || ''
        if (!code || code.length !== 6 || !/^\d{6}$/.test(code)) {
            setSecError('Please enter a valid 6-digit code')
            return
        }
        setSecLoading(true)
        setSecError('')
        try {
            const userInfo = localStorage.getItem('userInfo');
            const token = userInfo ? JSON.parse(userInfo).token : null;
            const res = await fetch(`${import.meta.env.VITE_API_BASE_URL || import.meta.env.VITE_API_URL}/api/users/2fa/disable`, {
                method: 'POST',
                headers: { 
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({ code })
            });
            if (res.ok) {
                setSecSuccess('Google Authenticator disabled successfully')
                getProfile().then(setProfile);
                setTimeout(closeSecModal, 1500)
            } else {
                const data = await res.json();
                setSecError(data.message || 'Failed to unbind authenticator');
            }
        } catch (error) {
            setSecError('Error unbinding authenticator');
        } finally {
            setSecLoading(false)
        }
    };

    const handleSetFundPassword = async () => {
        if (!secForm.currentPassword || !secForm.newFundPassword || secForm.newFundPassword !== secForm.confirmFundPassword) {
            toast('Please fill all fields correctly', 'error');
            return;
        }
        try {
            const userInfo = localStorage.getItem('userInfo');
            const token = userInfo ? JSON.parse(userInfo).token : null;
            const res = await fetch(`${import.meta.env.VITE_API_BASE_URL || import.meta.env.VITE_API_URL}/api/users/fund-password`, {
                method: 'POST',
                headers: { 
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}` 
                },
                body: JSON.stringify({
                    currentPassword: secForm.currentPassword,
                    newFundPassword: secForm.newFundPassword
                })
            });
            if (res.ok) {
                toast('Fund password set! 24h withdrawal lock active.', 'success');
                getProfile().then(setProfile);
                setSecModal('none');
            } else {
                const data = await res.json();
                toast(data.message || 'Failed to set fund password', 'error');
            }
        } catch (error) {
            toast('Error setting fund password', 'error');
        }
    };

    const handleBindAuthenticator = async () => {
        setSecError(''); setSecSuccess(''); setSecLoading(true)
        const code = secForm.otpCode || ''
        if (!code || code.length !== 6 || !/^\d{6}$/.test(code)) {
            setSecError('Please enter a valid 6-digit code')
            setSecLoading(false)
            return
        }
        
        try {
            const userInfo = localStorage.getItem('userInfo');
            const token = userInfo ? JSON.parse(userInfo).token : null;
            const res = await fetch(`${import.meta.env.VITE_API_BASE_URL || import.meta.env.VITE_API_URL}/api/users/2fa/enable`, {
                method: 'POST',
                headers: { 
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({ code, secret: authSecret }) // Sending the code and secret to verify
            });
            
            if (res.ok) {
                setSecLoading(false)
                setSecSuccess('Google Authenticator bound successfully!')
                getProfile().then(setProfile);
                setTimeout(closeSecModal, 1500)
            } else {
                const data = await res.json();
                setSecError(data.message || 'Failed to bind authenticator')
                setSecLoading(false)
            }
        } catch (error) {
            setSecError('Error binding authenticator')
            setSecLoading(false)
        }
    }

    const handleChange2FA = async () => {
        setSecError(''); setSecSuccess(''); setSecLoading(true)
        const oldCode = secForm.oldCode || ''
        const newCode = secForm.otpCode || ''
        if (!oldCode || oldCode.length !== 6 || !/^\d{6}$/.test(oldCode)) {
            setSecError('Please enter a valid 6-digit old code')
            setSecLoading(false)
            return
        }
        if (!newCode || newCode.length !== 6 || !/^\d{6}$/.test(newCode)) {
            setSecError('Please enter a valid 6-digit new code')
            setSecLoading(false)
            return
        }
        try {
            const userInfo = localStorage.getItem('userInfo');
            const token = userInfo ? JSON.parse(userInfo).token : null;
            const res = await fetch(`${import.meta.env.VITE_API_BASE_URL || import.meta.env.VITE_API_URL}/api/users/2fa/change`, {
                method: 'POST',
                headers: { 
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({ oldCode, newSecret: authSecret, newCode })
            });
            
            if (res.ok) {
                setSecLoading(false)
                setSecSuccess('Google Authenticator changed successfully!')
                getProfile().then(setProfile);
                setTimeout(closeSecModal, 1500)
            } else {
                const data = await res.json();
                setSecError(data.message || 'Failed to change authenticator')
                setSecLoading(false)
            }
        } catch (error) {
            setSecError('Error changing authenticator')
            setSecLoading(false)
        }
    }

    const handleChangePassword = async () => {
        setSecError(''); setSecSuccess(''); setSecLoading(true)
        try {
            if (!secForm.currentPassword || !secForm.newPassword) throw new Error('All fields are required')
            if (secForm.newPassword !== secForm.confirmPassword) throw new Error('Passwords do not match')
            if (secForm.newPassword.length < 6) throw new Error('Password must be at least 6 characters')
            await changePassword(secForm.currentPassword, secForm.newPassword)
            setSecSuccess('Password changed successfully')
            setTimeout(closeSecModal, 1500)
        } catch (e: any) { setSecError(e.message) } finally { setSecLoading(false) }
    }

    const handleChangeEmail = async () => {
        setSecError(''); setSecSuccess(''); setSecLoading(true)
        try {
            if (!secForm.newEmail || !secForm.password) throw new Error('All fields are required')
            await changeEmail(secForm.newEmail, secForm.password)
            setSecSuccess('Email changed successfully')
            getProfile().then(setProfile).catch(() => { })
            setTimeout(closeSecModal, 1500)
        } catch (e: any) { setSecError(e.message) } finally { setSecLoading(false) }
    }

    const handleBindPhone = async () => {
        setSecError(''); setSecSuccess(''); setSecLoading(true)
        try {
            if (!secForm.phone) throw new Error('Phone number is required')
            await updateUserProfile({ phone: secForm.phone })
            setSecSuccess('Phone updated successfully')
            getProfile().then(setProfile).catch(() => { })
            setTimeout(closeSecModal, 1500)
        } catch (e: any) { setSecError(e.message) } finally { setSecLoading(false) }
    }

    const handleDeleteAccount = async () => {
        setSecError(''); setSecSuccess(''); setSecLoading(true)
        try {
            if (!secForm.password) throw new Error('Password is required')
            if (secForm.confirmDelete !== 'DELETE') throw new Error('Type DELETE to confirm')
            await deleteAccountService(secForm.password)
            logoutUser()
            navigate('/login')
        } catch (e: any) { setSecError(e.message) } finally { setSecLoading(false) }
    }
    // Fetch orders when order tab is active
    useEffect(() => {
        // order tab removed
    }, [activeProfileTab])

    // Fetch notifications when message tab is active
    useEffect(() => {
        // message tab removed
    }, [activeProfileTab])

    // Fetch referral stats when invite tab is active
    useEffect(() => {
        if (activeProfileTab === 'invite') {
            getReferralStats().then(setReferralStats).catch(() => { })
        }
    }, [activeProfileTab])

    // Derived summary tiles for the invite dashboard — these were previously hardcoded
    // to "0" regardless of actual referral data.
    const referralFriendsCount = referralStats?.length ?? 0
    const referralTotalRewards = (referralStats ?? []).reduce((sum: number, r: any) => sum + (Number(r.rebateAmount) || 0), 0)
    const referralTopRebate = (referralStats ?? []).reduce((max: number, r: any) => Math.max(max, Number(r.rebateAmount) || 0), 0)




    useEffect(() => {
        if (profileLoaded && balanceLoaded) {
            setPageLoading(false)
        }
    }, [profileLoaded, balanceLoaded])

    useEffect(() => {
        getProfile().then(data => { setProfile(data); setProfileLoaded(true); }).catch(() => { setProfileLoaded(true); })
        getBalance().then(data => setUserBalance(data.balance || 0)).catch(() => { }).finally(() => { setBalanceLoading(false); setBalanceLoaded(true); })
        
        let ws: WebSocket | null = null;
        let retryTimeout: any;

        const connectWS = () => {
            try {
                // Initial fetch for instant data
                fetch('https://api.binance.com/api/v3/ticker/24hr?symbols=["BTCUSDT","ETHUSDT","SOLUSDT","BNBUSDT","XRPUSDT","AVAXUSDT","DOGEUSDT","ADAUSDT"]')
                    .then(r => r.json())
                    .then(data => {
                        if (Array.isArray(data)) {
                            setMarketData(prev => {
                                const next = { ...prev };
                                let hasChanges = false;
                                data.forEach(ticker => {
                                    const symbol = ticker.symbol.replace('USDT', '/USDT');
                                    next[symbol] = {
                                        price: parseFloat(ticker.lastPrice),
                                        change24h: parseFloat(ticker.priceChangePercent),
                                        volume24h: parseFloat(ticker.volume)
                                    };
                                    hasChanges = true;
                                });
                                return hasChanges ? next : prev;
                            });
                        }
                    }).catch(() => { });

                ws = new WebSocket('wss://stream.binance.com:9443/ws/!ticker@arr');
                ws.onmessage = (event) => {
                    try {
                        const data = JSON.parse(event.data);
                        if (Array.isArray(data)) {
                            setMarketData(prev => {
                                const next = { ...prev };
                                let hasChanges = false;
                                
                                const interestingSymbols = ['BTCUSDT', 'ETHUSDT', 'SOLUSDT', 'BNBUSDT', 'XRPUSDT', 'AVAXUSDT', 'DOGEUSDT', 'ADAUSDT'];
                                
                                data.forEach((ticker: any) => {
                                    if (interestingSymbols.includes(ticker.s)) {
                                        const symbol = ticker.s.replace('USDT', '/USDT');
                                        const price = parseFloat(ticker.c);
                                        const change24h = parseFloat(ticker.P);
                                        const volume24h = parseFloat(ticker.q);
                                        
                                        if (!next[symbol] || next[symbol].price !== price || next[symbol].change24h !== change24h) {
                                            next[symbol] = {
                                                ...next[symbol],
                                                price,
                                                change24h,
                                                volume24h
                                            };
                                            hasChanges = true;
                                        }
                                    }
                                });
                                return hasChanges ? next : prev;
                            });
                        }
                    } catch (e) {
                        // ignore parsing errors
                    }
                };

                ws.onclose = () => {
                    retryTimeout = setTimeout(connectWS, 3000);
                };
            } catch (error) {
                retryTimeout = setTimeout(connectWS, 3000);
            }
        };

        connectWS();

        return () => {
            if (ws) ws.close();
            clearTimeout(retryTimeout);
        }
    }, [])

    const userName = profile?.name || 'User'
    const userEmail = profile?.email || ''
    const userId = profile?.uid ? String(profile.uid) : '--------'
    const kycStatus = profile?.kycStatus || 'none'
    const userPhone = profile?.phone || 'Not set'

    // Referral code derived from user ID
    // Sharing your account UID here would silently never link referrals — the backend
    // matches new signups against `invitationCode`, a separate auto-generated field.
    const referralCode = profile?.invitationCode ? String(profile.invitationCode) : '----------'
    const referralLink = `${window.location.origin}/signup?ref=${referralCode}`

    const copyToClipboard = (text: string) => {
        navigator.clipboard.writeText(text).catch(() => { })
    }

    // Settings modals state
    const [showNicknameModal, setShowNicknameModal] = useState(false)
    const [nicknameInput, setNicknameInput] = useState('')
    const [nicknameSaving, setNicknameSaving] = useState(false)
    const [nicknameSuccess, setNicknameSuccess] = useState('')

    const [showOrderConfirmModal, setShowOrderConfirmModal] = useState(false)
    const [orderConfirmSettings, setOrderConfirmSettings] = useState<Record<string, boolean>>(() => {
        const saved = localStorage.getItem('orderConfirmSettings')
        return saved ? JSON.parse(saved) : { limit: true, market: true, stop: true }
    })

    const [showInviteCodeModal, setShowInviteCodeModal] = useState(false)
    const [inviteCodeInput, setInviteCodeInput] = useState('')
    const [inviteCodeSaving, setInviteCodeSaving] = useState(false)
    const [inviteCodeSuccess, setInviteCodeSuccess] = useState('')
    const [savedInviteCode, setSavedInviteCode] = useState(() => localStorage.getItem('superiorInviteCode') || '')

    const handleNicknameSave = async () => {
        if (!nicknameInput.trim()) return
        setNicknameSaving(true)
        setNicknameSuccess('')
        try {
            await updateUserProfile({ name: nicknameInput.trim() })
            setProfile((prev: any) => prev ? { ...prev, name: nicknameInput.trim() } : prev)
            setNicknameSuccess('Nickname updated successfully!')
            setTimeout(() => { setShowNicknameModal(false); setNicknameSuccess('') }, 1200)
        } catch {
            setNicknameSuccess('Failed to update nickname')
        } finally {
            setNicknameSaving(false)
        }
    }

    const handleOrderConfirmToggle = (key: string) => {
        setOrderConfirmSettings(prev => {
            const updated = { ...prev, [key]: !prev[key] }
            localStorage.setItem('orderConfirmSettings', JSON.stringify(updated))
            return updated
        })
    }

    const handleInviteCodeSave = () => {
        if (!inviteCodeInput.trim()) return
        setInviteCodeSaving(true)
        setInviteCodeSuccess('')
        // Simulate save (no backend endpoint for this yet)
        setTimeout(() => {
            localStorage.setItem('superiorInviteCode', inviteCodeInput.trim())
            setSavedInviteCode(inviteCodeInput.trim())
            setInviteCodeSuccess('Invitation code added successfully!')
            setInviteCodeSaving(false)
            setTimeout(() => { setShowInviteCodeModal(false); setInviteCodeSuccess('') }, 1200)
        }, 600)
    }

    // Tasks based on user progress
    const userTasks = [
        { id: 'kyc', name: 'Complete Identity Verification', desc: 'Verify your identity to unlock full trading features', reward: '5 USDT Bonus', done: kycStatus === 'verified', category: 'beginner' },
        { id: 'deposit', name: 'Make Your First Deposit', desc: 'Deposit any amount to start trading', reward: '2 USDT Cashback', done: userBalance > 0, category: 'beginner' },
        { id: 'trade', name: 'Complete First Trade', desc: 'Open and close your first spot trade', reward: '3 USDT Bonus', done: false, category: 'beginner' },
        { id: 'phone', name: 'Bind Phone Number', desc: 'Add your phone number for account security', reward: '1 USDT Bonus', done: profile?.phone ? true : false, category: 'beginner' },
        { id: 'invite', name: 'Invite a Friend', desc: 'Share your referral code and earn rewards', reward: '10 USDT per invite', done: false, category: 'limited' },
    ]



    const coinLabels: Record<string, string> = { BTC: 'Bitcoin', ETH: 'Ethereum', SOL: 'Solana', BNB: 'Binance', XRP: 'Ripple', AVAX: 'Avalanche', DOGE: 'Dogecoin', ADA: 'Cardano' }
    const coinIcons: Record<string, string> = {
        BTC: 'https://assets.coingecko.com/coins/images/1/small/bitcoin.png',
        ETH: 'https://assets.coingecko.com/coins/images/279/small/ethereum.png',
        SOL: 'https://assets.coingecko.com/coins/images/4128/small/solana.png',
        BNB: 'https://assets.coingecko.com/coins/images/825/small/bnb-icon2_2x.png',
        XRP: 'https://assets.coingecko.com/coins/images/44/small/xrp-symbol-white-128.webp',
        AVAX: 'https://assets.coingecko.com/coins/images/12559/small/Avalanche_Circle_RedWhite_Trans.png',
        DOGE: 'https://assets.coingecko.com/coins/images/5/small/dogecoin.png',
        ADA: 'https://assets.coingecko.com/coins/images/975/small/cardano.png',
    }
    const allMarketSymbols = ['BTC/USDT', 'ETH/USDT', 'SOL/USDT', 'BNB/USDT', 'XRP/USDT', 'AVAX/USDT', 'DOGE/USDT', 'ADA/USDT']

    const fmt = (n: number, digits = 2) => n ? `$${n.toLocaleString(undefined, { minimumFractionDigits: digits, maximumFractionDigits: digits })}` : '—'


    const btcPrice = marketData['BTC/USDT']?.price || 0
    const ethPrice = marketData['ETH/USDT']?.price || 0
    const assets = [
        {
            symbol: 'USDT', name: 'Tether',
            totalBalance: `${userBalance.toFixed(2)} USDT`,
            usdValue: `≈$${userBalance.toFixed(2)}`,
            walletBalance: `${userBalance.toFixed(2)}`,
            available: `${userBalance.toFixed(2)}`,
            pnl: '—'
        },
        {
            symbol: 'BTC',
            name: 'Bitcoin',
            totalBalance: `${(getSpotHoldings()['BTC'] || 0).toFixed(6)} BTC`,
            usdValue: `≈$${((getSpotHoldings()['BTC'] || 0) * (marketData['BTC/USDT']?.price || 0)).toFixed(2)}`,
            walletBalance: `${(getSpotHoldings()['BTC'] || 0).toFixed(6)}`,
            available: `${(getSpotHoldings()['BTC'] || 0).toFixed(6)}`,
            pnl: '—'
        },
        {
            symbol: 'ETH',
            name: 'Ethereum',
            totalBalance: `${(getSpotHoldings()['ETH'] || 0).toFixed(6)} ETH`,
            usdValue: `≈$${((getSpotHoldings()['ETH'] || 0) * (marketData['ETH/USDT']?.price || 0)).toFixed(2)}`,
            walletBalance: `${(getSpotHoldings()['ETH'] || 0).toFixed(6)}`,
            available: `${(getSpotHoldings()['ETH'] || 0).toFixed(6)}`,
            pnl: '—'
        }
    ]

    return (
        <Layout activePage="profile" hideMobileNav={true}>
            {/* Full-screen shimmer overlay — shown until profile + balance data are ready */}
            {pageLoading && (
                <div style={{
                    position: 'fixed', inset: 0, zIndex: 9999,
                    background: 'var(--ui-bg-primary)',
                    display: 'flex', flexDirection: 'column', gap: 16,
                    padding: '24px 16px', overflowY: 'hidden'
                }}>
                    {/* Header skeleton */}
                    <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 8 }}>
                        <div className="shimmer-box" style={{ width: 36, height: 36, borderRadius: '50%' }} />
                        <div className="shimmer-box" style={{ width: 120, height: 20, borderRadius: 8 }} />
                    </div>
                    {/* Profile card skeleton */}
                    <div style={{ background: 'var(--ui-bg-secondary)', borderRadius: 16, padding: 20, display: 'flex', flexDirection: 'column', gap: 16 }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
                            <div className="shimmer-box" style={{ width: 64, height: 64, borderRadius: '50%', flexShrink: 0 }} />
                            <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 8 }}>
                                <div className="shimmer-box" style={{ width: '60%', height: 18, borderRadius: 6 }} />
                                <div className="shimmer-box" style={{ width: '80%', height: 13, borderRadius: 6 }} />
                            </div>
                        </div>
                        <div style={{ display: 'flex', gap: 12 }}>
                            <div className="shimmer-box" style={{ flex: 1, height: 40, borderRadius: 10 }} />
                            <div className="shimmer-box" style={{ flex: 1, height: 40, borderRadius: 10 }} />
                        </div>
                    </div>
                    {/* KYC banner skeleton */}
                    <div className="shimmer-box" style={{ width: '100%', height: 60, borderRadius: 12 }} />
                    {/* Balance card skeleton */}
                    <div style={{ background: 'var(--ui-bg-secondary)', borderRadius: 16, padding: 20, display: 'flex', flexDirection: 'column', gap: 12 }}>
                        <div className="shimmer-box" style={{ width: 120, height: 14, borderRadius: 6 }} />
                        <div className="shimmer-box" style={{ width: '70%', height: 36, borderRadius: 8 }} />
                        <div className="shimmer-box" style={{ width: '45%', height: 13, borderRadius: 6 }} />
                        <div style={{ display: 'flex', gap: 12, marginTop: 4 }}>
                            <div className="shimmer-box" style={{ flex: 1, height: 44, borderRadius: 10 }} />
                            <div className="shimmer-box" style={{ flex: 1, height: 44, borderRadius: 10 }} />
                        </div>
                    </div>
                    {/* Table header skeleton */}
                    <div style={{ display: 'flex', gap: 8 }}>
                        {[100, 110, 90, 80, 70].map((w, i) => (
                            <div key={i} className="shimmer-box" style={{ width: w, height: 13, borderRadius: 6 }} />
                        ))}
                    </div>
                    {/* Table rows skeleton */}
                    {[1, 2, 3].map(i => (
                        <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                            <div className="shimmer-box" style={{ width: 32, height: 32, borderRadius: '50%', flexShrink: 0 }} />
                            <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 6 }}>
                                <div className="shimmer-box" style={{ width: '40%', height: 13, borderRadius: 6 }} />
                                <div className="shimmer-box" style={{ width: '60%', height: 11, borderRadius: 6 }} />
                            </div>
                            <div className="shimmer-box" style={{ width: 70, height: 13, borderRadius: 6 }} />
                        </div>
                    ))}
                </div>
            )}
            {/* Hidden file input for avatar upload */}
            <input
                ref={avatarInputRef}
                type="file"
                accept="image/*"
                style={{ display: 'none' }}
                onChange={handleAvatarChange}
            />
            {/* Mobile Profile View */}
            <div className="mobile-profile-view">
                {/* Mobile Sidebar Menu — matches home drawer style */}
                {showMobileMenu && (
                    <div className="mpv-sidebar-overlay" onClick={() => setShowMobileMenu(false)}>
                        <div className="mpv-sidebar" onClick={(e) => e.stopPropagation()} style={{ background: 'var(--ui-bg-primary)', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>

                            {/* Top bar — logo + close */}
                            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '16px 20px', borderBottom: '1px solid #1c1c2c', flexShrink: 0 }}>
                                <BicoinLogo height={32} />
                                <button onClick={() => setShowMobileMenu(false)} style={{ background: 'none', border: 'none', color: 'var(--ui-text-muted)', cursor: 'pointer', padding: 4, display: 'flex', alignItems: 'center' }}>
                                    <X size={22} />
                                </button>
                            </div>

                            {/* Scrollable content */}
                            <div style={{ flex: 1, overflowY: 'auto', display: 'flex', flexDirection: 'column' }}>

                                {/* User card */}
                                <div style={{ padding: '12px 16px 0' }}>
                                    <div style={{ background: 'var(--ui-bg-card)', border: '1px solid var(--ui-border)', borderRadius: 12, padding: '14px 16px' }}>
                                        <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 10 }}>
                                            <div style={{ position: 'relative', width: 44, height: 44, flexShrink: 0 }} onClick={handleAvatarClick}>
                                                <img src={avatarPreview || '/avatar.webp'} alt={userName} style={{ width: 44, height: 44, borderRadius: '50%', objectFit: 'cover', border: '2px solid var(--ui-border)' }} />
                                                <div style={{ position: 'absolute', bottom: 0, right: 0, background: '#1CD4A7', borderRadius: '50%', width: 18, height: 18, display: 'flex', alignItems: 'center', justifyContent: 'center', border: '2px solid var(--ui-bg-primary)', cursor: 'pointer' }}>
                                                    <Camera size={10} color="#000" />
                                                </div>
                                            </div>
                                            <div style={{ flex: 1, minWidth: 0 }}>
                                                <div style={{ color: 'var(--ui-text-primary)', fontWeight: 600, fontSize: 15, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{userName}</div>
                                                <div style={{ color: 'var(--ui-text-muted)', fontSize: 12, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{userEmail}</div>
                                            </div>
                                        </div>
                                        {/* UID with copy */}
                                        <div onClick={() => { navigator.clipboard.writeText(userId.replace('UID: ', '')); }} style={{ display: 'flex', alignItems: 'center', gap: 6, background: 'var(--ui-bg-primary)', borderRadius: 6, padding: '6px 10px', cursor: 'pointer', marginBottom: 10 }}>
                                            <span style={{ color: 'var(--ui-text-muted)', fontSize: 12, flex: 1 }}>{userId}</span>
                                            <Copy size={11} color="#71717a" />
                                        </div>
                                        {/* Verified badge */}
                                        <div onClick={() => { setShowMobileMenu(false); setActiveProfileTab('verification'); }} style={{ display: 'inline-flex', alignItems: 'center', gap: 4, padding: '3px 8px', borderRadius: 6, fontSize: 11, fontWeight: 600, background: kycStatus === 'verified' ? 'rgba(16,185,129,0.1)' : 'rgba(246,70,93,0.15)', color: kycStatus === 'verified' ? '#10b981' : '#F6465D', cursor: 'pointer' }}>
                                            <ShieldCheck size={12} />
                                            <span>{kycStatus === 'verified' ? 'Verified' : kycStatus === 'pending' ? 'Pending' : 'Unverified'}</span>
                                        </div>
                                    </div>
                                </div>

                                {/* Divider */}
                                <div style={{ height: 1, background: 'var(--ui-bg-elevated)', margin: '12px 0 0' }} />

                                {/* Navigation */}
                                <div style={{ padding: '8px 12px', display: 'flex', flexDirection: 'column', gap: 2 }}>
                                    <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--ui-text-muted)', textTransform: 'uppercase', letterSpacing: '0.05em', padding: '4px 8px 8px' }}>Profile</div>
                                    {[
                                        { icon: <LayoutDashboard size={20} />, label: 'Overview',       tab: 'overview' as const },
                                        { icon: <Shield size={20} />,          label: 'Verification',   tab: 'verification' as const },
                                        { icon: <Lock size={20} />,            label: 'Security',       tab: 'security' as const },
                                        { icon: <Users size={20} />,           label: 'Invite Friends', tab: 'invite' as const },
                                        { icon: <Settings size={20} />,        label: 'Settings',       tab: 'settings' as const },
                                    ].map(({ icon, label, tab }) => (
                                        <div key={tab} onClick={() => { setShowMobileMenu(false); setActiveProfileTab(tab); }}
                                            style={{ display: 'flex', alignItems: 'center', gap: 14, padding: '11px 8px', borderRadius: 10, cursor: 'pointer', background: activeProfileTab === tab ? 'rgba(28,212,167,0.08)' : 'transparent', color: activeProfileTab === tab ? '#1CD4A7' : '#e4e4e7', fontWeight: activeProfileTab === tab ? 600 : 400, fontSize: 15 }}>
                                            <span style={{ color: activeProfileTab === tab ? '#1CD4A7' : '#71717a', display: 'flex' }}>{icon}</span>
                                            {label}
                                        </div>
                                    ))}
                                </div>

                                {/* Logout at bottom */}
                                <div style={{ marginTop: 'auto', padding: '16px' }}>
                                    <button onClick={handleLogout} style={{ width: '100%', background: 'transparent', border: '1px solid rgba(239,68,68,0.3)', borderRadius: 12, padding: '12px', color: '#EF4444', fontSize: 14, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8 }}>
                                        <LogOut size={16} />
                                        Logout
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                )}

                {/* Profile Header */}
                <div className="mpv-page-header">
                    <div className="mpv-header-left">
                        <button className="mpv-back-btn" onClick={() => {
                            if (activeProfileTab !== 'overview') {
                                setActiveProfileTab('overview');
                            } else {
                                navigate('/dashboard/home');
                            }
                        }}>
                            <ArrowLeft size={20} />
                        </button>
                        <span className="mpv-page-title">
                            {activeProfileTab === 'overview' && 'Profile'}
                            {activeProfileTab === 'verification' && 'Verification'}
                            {activeProfileTab === 'security' && 'Security'}
                            {activeProfileTab === 'invite' && 'Invite friends'}
                            {activeProfileTab === 'settings' && 'Settings'}
                        </span>
                    </div>
                    <button className="mpv-menu-btn" onClick={() => setShowMobileMenu(true)}>
                        <Menu size={20} />
                    </button>
                </div>

                {/* Overview Tab - Mobile */}
                {activeProfileTab === 'overview' && (
                    <div className="mpv-content">
                        {/* Profile Info Card */}
                        <div className="mpv-profile-card">
                            <div className="mpv-avatar-section">
                                <div className="mpv-avatar" onClick={handleAvatarClick} style={{ cursor: 'pointer', position: 'relative' }}>
                                    <img src={avatarPreview || '/avatar.webp'} alt={userName} />
                                    <div style={{ position: 'absolute', bottom: 0, right: 0, background: '#1CD4A7', borderRadius: '50%', width: 24, height: 24, display: 'flex', alignItems: 'center', justifyContent: 'center', border: '2px solid var(--ui-bg-primary)' }}>
                                        <Camera size={13} color="#fff" />
                                    </div>
                                    {avatarUploading && <div style={{ position: 'absolute', inset: 0, background: 'rgba(0,0,0,0.5)', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--ui-text-primary)', fontSize: 10 }}>...</div>}
                                </div>
                                <div className="mpv-user-info">
                                    <h2 className="mpv-name">{userName}</h2>
                                    <p className="mpv-email">{userEmail}</p>
                                </div>
                            </div>

                            <div className="mpv-id-row">
                                <div className="mpv-id-item">
                                    <span className="mpv-id-label">UID</span>
                                    <div className="mpv-id-value">
                                        <span>{userId}</span>
                                        <Copy size={12} className="mpv-copy-icon" />
                                    </div>
                                </div>
                                <div className="mpv-id-item" onClick={() => navigate('/dashboard/kyc')}>
                                    <span className="mpv-id-label">ID Verification</span>
                                    <div className="mpv-id-value clickable">
                                        <span>{kycStatus === 'verified' ? 'Verified' : kycStatus === 'pending' ? 'Pending' : 'Not verified'}</span>
                                        <ChevronRight size={12} />
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* KYC Banner — only when not verified */}
                        {kycStatus !== 'verified' && (
                            <div style={{ background: 'rgba(245,158,11,0.08)', border: '1px solid rgba(245,158,11,0.2)', borderRadius: 12, padding: '12px 14px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12 }}>
                                <div>
                                    <div style={{ color: '#F59E0B', fontWeight: 700, fontSize: 13, marginBottom: 2 }}>Identity not verified</div>
                                    <div style={{ color: 'var(--ui-text-muted)', fontSize: 12 }}>Complete KYC to unlock full trading features</div>
                                </div>
                                <button onClick={() => navigate('/dashboard/kyc')} style={{ background: '#F59E0B', border: 'none', borderRadius: 8, color: '#000', fontSize: 12, fontWeight: 700, padding: '6px 12px', cursor: 'pointer', flexShrink: 0, fontFamily: 'inherit' }}>Verify</button>
                            </div>
                        )}

                        {/* Valuation Section */}
                        <div className="mpv-valuation">
                            <div className="mpv-val-label">
                                <span>Valuation</span>
                                {balanceHidden
                                    ? <EyeOff size={14} className="mpv-eye-icon cursor-pointer" onClick={() => setBalanceHidden(false)} />
                                    : <Eye size={14} className="mpv-eye-icon cursor-pointer" onClick={() => setBalanceHidden(true)} />
                                }
                            </div>
                            <div className="mpv-val-amount">
                                <span className="mpv-btc-value">{balanceLoading ? <span className="balance-skeleton" /> : balanceHidden ? '****' : `$${userBalance.toFixed(2)} USDT`}</span>
                            </div>
                            <span className="mpv-usd-value">{balanceLoading ? <span className="balance-skeleton balance-skeleton-sm" /> : balanceHidden ? '****' : `≈$${userBalance.toFixed(2)}`}</span>
                        </div>

                        {/* Action Buttons */}
                        <div className="mpv-actions">
                            <button className="mpv-btn-deposit" onClick={() => navigate('/dashboard/deposit')}>Deposit</button>
                            <button className="mpv-btn-withdraw" onClick={() => navigate('/dashboard/withdraw')}>Withdraw</button>
                        </div>
                        <div className="mpv-actions" style={{ marginTop: 8 }}>
                            <button className="mpv-btn-withdraw" onClick={() => navigate('/dashboard/transfer')}>Transfer</button>
                            <button className="mpv-btn-withdraw" onClick={() => navigate('/dashboard/convert')}>Convert</button>
                        </div>

                        {/* Market Section */}
                        {/* Assets Table — matches desktop */}
                        <div className="mpv-market-section">
                            <div className="mpv-market-header">
                                <span className="mpv-market-title">My Assets</span>
                            </div>

                            {/* Mobile assets cards */}
                            <div style={{ display: 'flex', flexDirection: 'column', gap: 1, background: 'var(--ui-bg-elevated)', borderRadius: 12, overflow: 'hidden', marginTop: 12 }}>
                                {/* Header row */}
                                <div style={{ display: 'flex', padding: '10px 14px', background: 'var(--ui-bg-card)' }}>
                                    <span style={{ flex: 1, fontSize: 11, color: 'var(--ui-text-muted)', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px' }}>Coin</span>
                                    <span style={{ width: 100, fontSize: 11, color: 'var(--ui-text-muted)', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px', textAlign: 'right' }}>Balance</span>
                                    <span style={{ width: 80, fontSize: 11, color: 'var(--ui-text-muted)', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px', textAlign: 'right' }}>Action</span>
                                </div>
                                {assets.map((asset) => (
                                    <div key={asset.symbol} style={{ display: 'flex', alignItems: 'center', padding: '12px 14px', background: 'var(--ui-bg-primary)', borderTop: '1px solid #1C1C2C' }}>
                                        {/* Coin */}
                                        <div style={{ flex: 1, display: 'flex', alignItems: 'center', gap: 10 }}>
                                            <img
                                                src={asset.symbol === 'USDT'
                                                    ? 'https://assets.coingecko.com/coins/images/325/small/Tether.png'
                                                    : asset.symbol === 'BTC'
                                                    ? 'https://assets.coingecko.com/coins/images/1/small/bitcoin.png'
                                                    : 'https://assets.coingecko.com/coins/images/279/small/ethereum.png'}
                                                alt={asset.symbol}
                                                style={{ width: 32, height: 32, borderRadius: '50%', objectFit: 'cover', flexShrink: 0 }}
                                            />
                                            <div>
                                                <div style={{ color: 'var(--ui-text-primary)', fontWeight: 700, fontSize: 14 }}>{asset.symbol}</div>
                                                <div style={{ color: 'var(--ui-text-muted)', fontSize: 11 }}>{asset.name}</div>
                                            </div>
                                        </div>
                                        {/* Balance */}
                                        <div style={{ width: 100, textAlign: 'right' }}>
                                            <div style={{ color: 'var(--ui-text-primary)', fontWeight: 600, fontSize: 13 }}>{asset.totalBalance}</div>
                                            <div style={{ color: 'var(--ui-text-muted)', fontSize: 11 }}>{asset.usdValue}</div>
                                        </div>
                                        {/* Action */}
                                        <div style={{ width: 80, textAlign: 'right' }}>
                                            <button
                                                onClick={() => navigate(`/dashboard/trade?pair=${asset.symbol}/USDT`)}
                                                style={{ background: 'rgba(28,212,167,0.1)', border: '1px solid rgba(28,212,167,0.2)', borderRadius: 6, color: '#1CD4A7', fontSize: 12, fontWeight: 600, padding: '4px 10px', cursor: 'pointer', fontFamily: 'inherit' }}
                                            >
                                                Trade
                                            </button>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>
                )}

                {/* Verification Tab - Mobile */}
                {activeProfileTab === 'verification' && (
                    <div className="mpv-content mpv-verification">
                        {/* User Info */}
                        <div className="mpv-ver-user-card">
                            <div className="mpv-ver-avatar">
                                <User size={24} />
                            </div>
                            <div className="mpv-ver-user-info">
                                <span className="mpv-ver-email">{userEmail}</span>
                                <span className={`mpv-ver-status ${kycStatus === 'verified' ? 'verified' : ''}`}>
                                    {kycStatus === 'verified' ? 'Verified' : kycStatus === 'pending' ? 'Pending' : 'Unverified'}
                                </span>
                            </div>
                        </div>

                        {/* Verification Section */}
                        <div className="mpv-ver-section">
                            <h3 className="mpv-ver-title">User-verification</h3>
                            <p className="mpv-ver-desc">Only takes 3-5 minutes to complete the identity verification to protect your account from fraud and illegal risks.</p>
                            {kycStatus === 'verified' ? (
                                <div className="mpv-ver-verified-badge" style={{ display: 'flex', alignItems: 'center', gap: 8, color: '#1CD4A7', fontWeight: 600, padding: '8px 0' }}>
                                    <CheckSquare size={16} /> Verified
                                </div>
                            ) : kycStatus === 'pending' ? (
                                <div style={{ color: '#F59E0B', fontWeight: 600, padding: '8px 0' }}>Verification in progress...</div>
                            ) : (
                                <button className="mpv-ver-btn" onClick={() => navigate('/dashboard/kyc')}>Verify</button>
                            )}
                        </div>

                        {/* Account Limits */}
                        <div className="mpv-limits-section">
                            <div className="mpv-limits-header">
                                <Lock size={16} />
                                <span>Account limits</span>
                            </div>
                            <div className="mpv-limit-row">
                                <span className="mpv-limit-label">Crypto deposit limit</span>
                                <span className="mpv-limit-value teal">Enable deposit</span>
                            </div>
                            <div className="mpv-limit-row">
                                <span className="mpv-limit-label">Crypto withdrawal limit</span>
                                <div className="mpv-limit-values">
                                    <span>20000 USDT Daily</span>
                                    <span>20000 USDT Monthly</span>
                                </div>
                            </div>
                            <div className="mpv-limit-row">
                                <span className="mpv-limit-label">P2p transaction limit</span>
                                <span className="mpv-limit-value teal">Enable transaction</span>
                            </div>
                        </div>

                        {/* Verification Levels */}
                        <div className="mpv-ver-levels">
                            <div className="mpv-ver-level">
                                <div className="mpv-ver-level-header">
                                    <CheckSquare size={16} className="mpv-ver-icon success" />
                                    <span>User-Verification</span>
                                </div>
                                <p className="mpv-ver-level-limit">Crypto withdrawal limit 2000 USDT Daily</p>
                                <div className="mpv-ver-reqs">
                                    <span className="mpv-ver-req-label">Require</span>
                                    <div className="mpv-ver-req-item"><span className="mpv-req-dot"></span>Applicant data</div>
                                    <div className="mpv-ver-req-item"><span className="mpv-req-dot"></span>Identity document</div>
                                    <div className="mpv-ver-req-item"><span className="mpv-req-dot"></span>Selfie with document</div>
                                </div>
                            </div>

                            <div className="mpv-ver-level">
                                <div className="mpv-ver-level-header">
                                    <X size={16} className="mpv-ver-icon error" />
                                    <span>Additional proof of address</span>
                                </div>
                                <p className="mpv-ver-level-limit">Crypto withdrawal limit 20000 USDT Daily</p>
                                <div className="mpv-ver-reqs">
                                    <span className="mpv-ver-req-label">Require</span>
                                    <div className="mpv-ver-req-item"><span className="mpv-req-dot"></span>Identity document</div>
                                    <div className="mpv-ver-req-item"><span className="mpv-req-dot"></span>Proof of residence</div>
                                </div>
                            </div>
                        </div>
                    </div>
                )}

                {/* Security Tab - Mobile */}
                {activeProfileTab === 'security' && (
                    <div className="mpv-content mpv-security">
                        {/* Authentication Method */}
                        <div className="mpv-sec-section">
                            <h3 className="mpv-sec-title">Authentication method</h3>

                            <div className="mpv-sec-item">
                                <div className="mpv-sec-icon">G</div>
                                <div className="mpv-sec-info">
                                    <span className="mpv-sec-name">Google Authenticator</span>
                                    <span className="mpv-sec-desc">API Secure verification when withdrawing, retrieving passwords, modifying security settings and managing API</span>
                                </div>
                                {profile?.isGoogleAuthenticatorEnabled ? (
                                    <div style={{ display: 'flex', gap: 8 }}>
                                        <button className="mpv-sec-btn" onClick={() => {
                                            setSecForm({ otpCode: '' });
                                            setSecModal('disable-2fa');
                                        }}>Unbind</button>
                                        <button className="mpv-sec-btn" onClick={() => {
                                            setSecForm({ oldCode: '', otpCode: '' });
                                            setAuthStep(1);
                                            generateNewSecret();
                                            setSecModal('change-2fa');
                                        }}>Change</button>
                                    </div>
                                ) : (
                                    <button className="mpv-sec-btn" onClick={() => {
                                        setSecForm({});
                                        setAuthStep(1);
                                        generateNewSecret();
                                        setSecModal('authenticator');
                                    }}>Bind</button>
                                )}
                            </div>

                            <div className="mpv-sec-item">
                                <div className="mpv-sec-icon"><Smartphone size={16} /></div>
                                <div className="mpv-sec-info">
                                    <span className="mpv-sec-name">Phone number</span>
                                    <span className="mpv-sec-desc">Receive verification SMS that is used to withdraw, change the password or security settings</span>
                                </div>
                                <div className="mpv-sec-right">
                                    <span className="mpv-sec-value">{userPhone}</span>
                                    <button className="mpv-sec-btn" onClick={() => { setSecForm({ phone: '' }); setSecModal('phone'); }}>{userPhone !== 'Not set' ? 'Change' : 'Bind'}</button>
                                </div>
                            </div>

                            <div className="mpv-sec-item">
                                <div className="mpv-sec-icon"><Mail size={16} /></div>
                                <div className="mpv-sec-info">
                                    <span className="mpv-sec-name">Email address</span>
                                    <span className="mpv-sec-desc">Used when logging in, withdrawing and modifying security settings</span>
                                </div>
                                <div className="mpv-sec-right">
                                    <span className="mpv-sec-value">{userEmail}</span>
                                    <button className="mpv-sec-btn" onClick={() => { setSecForm({ newEmail: '', password: '' }); setSecModal('email'); }}>Change</button>
                                </div>
                            </div>
                        </div>

                        {/* Advanced Security */}
                        <div className="mpv-sec-section">
                            <h3 className="mpv-sec-title">Advanced Security</h3>

                            <div className="mpv-sec-item">
                                <div className="mpv-sec-icon"><Lock size={16} /></div>
                                <div className="mpv-sec-info">
                                    <span className="mpv-sec-name">Password</span>
                                    <span className="mpv-sec-desc">Used to manage your account login password</span>
                                </div>
                                <button className="mpv-sec-btn" onClick={() => { setSecForm({ currentPassword: '', newPassword: '', confirmPassword: '' }); setSecModal('password'); }}>Change</button>
                            </div>

                            <div className="mpv-sec-item">
                                <div className="mpv-sec-icon"><ShieldCheck size={16} /></div>
                                <div className="mpv-sec-info">
                                    <span className="mpv-sec-name">Fund Password</span>
                                    <span className="mpv-sec-desc">Set a password for withdrawals to increase fund security</span>
                                </div>
                                <button className="mpv-sec-btn" onClick={() => { setSecForm({ currentPassword: '', newFundPassword: '', confirmFundPassword: '' }); setSecModal('fund-password'); }}>{profile?.hasFundPassword ? 'Change' : 'Set'}</button>
                            </div>

                            <div className="mpv-sec-item">
                                <div className="mpv-sec-icon"><Wallet size={16} /></div>
                                <div className="mpv-sec-info">
                                    <span className="mpv-sec-name">Address management</span>
                                    <span className="mpv-sec-desc">After setting as a trust address, withdrawals will be exempt from security verification</span>
                                </div>
                                <button className="mpv-sec-btn" onClick={() => navigate('/dashboard/deposit')}>Manage</button>
                            </div>
                        </div>

                        {/* Account Management */}
                        <div className="mpv-sec-section">
                            <h3 className="mpv-sec-title">Account Management</h3>

                            <div className="mpv-sec-item">
                                <div className="mpv-sec-icon"><Monitor size={16} /></div>
                                <div className="mpv-sec-info">
                                    <span className="mpv-sec-name">My device</span>
                                    <span className="mpv-sec-desc">For managing logged-in devices and viewing device history</span>
                                </div>
                                <button className="mpv-sec-btn" onClick={() => setSecModal('device')}>Manage</button>
                            </div>

                            <div className="mpv-sec-item">
                                <div className="mpv-sec-icon"><Activity size={16} /></div>
                                <div className="mpv-sec-info">
                                    <span className="mpv-sec-name">Account activity</span>
                                    <span className="mpv-sec-desc">Last login: {profile?.lastLogin ? new Date(profile.lastLogin).toLocaleString() : 'N/A'}</span>
                                </div>
                                <button className="mpv-sec-btn" onClick={() => setSecModal('activity')}>Manage</button>
                            </div>

                            <div className="mpv-sec-item">
                                <div className="mpv-sec-icon"><Trash2 size={16} /></div>
                                <div className="mpv-sec-info">
                                    <span className="mpv-sec-name">Delete account</span>
                                    <span className="mpv-sec-desc">After deleting your account, you will never be able to re-register this account and its sub-account email, mobile phone number, and identity information.</span>
                                </div>
                                <button className="mpv-sec-btn danger" onClick={() => { setSecForm({ password: '', confirmDelete: '' }); setSecModal('delete'); }}>Delete</button>
                            </div>
                        </div>

                        {/* Security Modals */}
                        {secModal !== 'none' && (
                            <div className="mpv-sec-modal-overlay" onClick={closeSecModal}>
                                <div className="mpv-sec-modal" onClick={e => e.stopPropagation()}>
                                    <button className="mpv-sec-modal-close" onClick={closeSecModal}><X size={18} /></button>

                                    {secModal === 'password' && (
                                        <>
                                            <h3 className="mpv-sec-modal-title"><Lock size={18} /> Change Password</h3>
                                            <div className="mpv-sec-modal-field">
                                                <label>Current Password</label>
                                                <input type="password" placeholder="Enter current password" value={secForm.currentPassword || ''} onChange={e => setSecForm(p => ({ ...p, currentPassword: e.target.value }))} />
                                            </div>
                                            <div className="mpv-sec-modal-field">
                                                <label>New Password</label>
                                                <input type="password" placeholder="Enter new password" value={secForm.newPassword || ''} onChange={e => setSecForm(p => ({ ...p, newPassword: e.target.value }))} />
                                            </div>
                                            <div className="mpv-sec-modal-field">
                                                <label>Confirm New Password</label>
                                                <input type="password" placeholder="Confirm new password" value={secForm.confirmPassword || ''} onChange={e => setSecForm(p => ({ ...p, confirmPassword: e.target.value }))} />
                                            </div>
                                            <button className="mpv-sec-modal-submit" disabled={secLoading} onClick={handleChangePassword}>{secLoading ? 'Saving...' : 'Change Password'}</button>
                                        </>
                                    )}

                                    {secModal === 'email' && (
                                        <>
                                            <h3 className="mpv-sec-modal-title"><Mail size={18} /> Change Email</h3>
                                            <p className="mpv-sec-modal-hint">Current: {userEmail}</p>
                                            <div className="mpv-sec-modal-field">
                                                <label>New Email</label>
                                                <input type="email" placeholder="Enter new email" value={secForm.newEmail || ''} onChange={e => setSecForm(p => ({ ...p, newEmail: e.target.value }))} />
                                            </div>
                                            <div className="mpv-sec-modal-field">
                                                <label>Password</label>
                                                <input type="password" placeholder="Enter your password" value={secForm.password || ''} onChange={e => setSecForm(p => ({ ...p, password: e.target.value }))} />
                                            </div>
                                            <button className="mpv-sec-modal-submit" disabled={secLoading} onClick={handleChangeEmail}>{secLoading ? 'Saving...' : 'Change Email'}</button>
                                        </>
                                    )}

                                    {secModal === 'phone' && (
                                        <>
                                            <h3 className="mpv-sec-modal-title"><Smartphone size={18} /> {userPhone !== 'Not set' ? 'Change' : 'Bind'} Phone</h3>
                                            {userPhone !== 'Not set' && <p className="mpv-sec-modal-hint">Current: {userPhone}</p>}
                                            <div className="mpv-sec-modal-field">
                                                <label>Phone Number</label>
                                                <input type="tel" placeholder="Enter phone number" value={secForm.phone || ''} onChange={e => setSecForm(p => ({ ...p, phone: e.target.value }))} />
                                            </div>
                                            <button className="mpv-sec-modal-submit" disabled={secLoading} onClick={handleBindPhone}>{secLoading ? 'Saving...' : 'Save Phone'}</button>
                                        </>
                                    )}

                                    {secModal === 'delete' && (
                                        <>
                                            <h3 className="mpv-sec-modal-title" style={{ color: '#ef4444' }}><AlertTriangle size={18} /> Delete Account</h3>
                                            <p className="mpv-sec-modal-hint" style={{ color: '#ef4444' }}>This action is permanent and cannot be undone. All your data, including trading history and balances, will be lost.</p>
                                            <div className="mpv-sec-modal-field">
                                                <label>Password</label>
                                                <input type="password" placeholder="Enter your password" value={secForm.password || ''} onChange={e => setSecForm(p => ({ ...p, password: e.target.value }))} />
                                            </div>
                                            <div className="mpv-sec-modal-field">
                                                <label>Type DELETE to confirm</label>
                                                <input type="text" placeholder="DELETE" value={secForm.confirmDelete || ''} onChange={e => setSecForm(p => ({ ...p, confirmDelete: e.target.value }))} />
                                            </div>
                                            <button className="mpv-sec-modal-submit danger" disabled={secLoading} onClick={handleDeleteAccount}>{secLoading ? 'Deleting...' : 'Delete My Account'}</button>
                                        </>
                                    )}

                                    {secModal === 'device' && (
                                        <>
                                            <h3 className="mpv-sec-modal-title"><Monitor size={18} /> My Devices</h3>
                                            <div className="mpv-sec-device-list" style={{ maxHeight: 350, overflowY: 'auto' }}>
                                                {deviceSessions.length > 0 ? (
                                                    deviceSessions.map((session) => (
                                                        <div key={session._id} className={`mpv-sec-device-item ${session.isCurrent ? 'active' : ''}`} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '12px 0', borderBottom: '1px solid #2a2a3a' }}>
                                                            <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                                                                <Monitor size={20} />
                                                                <div>
                                                                    <span className="mpv-sec-device-name" style={{ display: 'block', color: 'var(--ui-text-primary)', fontSize: 14, fontWeight: 500 }}>
                                                                        {session.deviceType || 'Unknown Device'}
                                                                    </span>
                                                                    <span className="mpv-sec-device-detail" style={{ display: 'block', color: 'var(--ui-text-muted)', fontSize: 12, marginTop: 2 }}>
                                                                        {session.location || 'Unknown Location'} · {session.ipAddress}
                                                                    </span>
                                                                    <span style={{ display: 'block', color: 'var(--ui-text-muted)', fontSize: 11, marginTop: 2 }}>
                                                                        Last active: {new Date(session.lastActive).toLocaleString()}
                                                                    </span>
                                                                </div>
                                                            </div>
                                                            {session.isCurrent ? (
                                                                <span className="mpv-sec-device-badge" style={{ background: 'rgba(28,212,167,0.1)', color: '#1CD4A7', padding: '2px 8px', borderRadius: 4, fontSize: 12 }}>Active</span>
                                                            ) : (
                                                                <button
                                                                    onClick={() => handleRevokeDevice(session.tokenHash)}
                                                                    style={{ background: '#ef4444', color: 'var(--ui-text-primary)', border: 'none', padding: '4px 10px', borderRadius: 4, cursor: 'pointer', fontSize: 12 }}
                                                                >
                                                                    Logout
                                                                </button>
                                                            )}
                                                        </div>
                                                    ))
                                                ) : (
                                                    <p style={{ color: 'var(--ui-text-muted)', fontSize: 13, textAlign: 'center', padding: '20px 0' }}>No active devices found</p>
                                                )}
                                            </div>
                                        </>
                                    )}

                                    {secModal === 'activity' && (
                                        <>
                                            <h3 className="mpv-sec-modal-title"><Activity size={18} /> Account Activity</h3>
                                            <div className="mpv-sec-activity-list">
                                                <div className="mpv-sec-activity-item">
                                                    <span className="mpv-sec-activity-action">Login</span>
                                                    <span className="mpv-sec-activity-time">{profile?.lastLogin ? new Date(profile.lastLogin).toLocaleString() : 'N/A'}</span>
                                                    <span className="mpv-sec-activity-detail">{navigator.platform} · {navigator.userAgent.includes('Mobile') ? 'Mobile' : 'Desktop'}</span>
                                                </div>
                                                <div className="mpv-sec-activity-item">
                                                    <span className="mpv-sec-activity-action">Account Created</span>
                                                    <span className="mpv-sec-activity-time">{profile?.createdAt ? new Date(profile.createdAt).toLocaleString() : 'N/A'}</span>
                                                </div>
                                            </div>
                                        </>
                                    )}

                                    {secModal === 'authenticator' && (
                                        <>
                                            <h3 className="mpv-sec-modal-title"><Shield size={18} /> Google Authenticator</h3>

                                            {authStep === 1 && (
                                                <div className="mpv-auth-step">
                                                    <p className="mpv-sec-modal-hint">Step 1: Download Google Authenticator app from your App Store or Play Store.</p>
                                                    <div className="mpv-auth-stores">
                                                        <div className="mpv-auth-store-badge">📱 App Store</div>
                                                        <div className="mpv-auth-store-badge">📱 Play Store</div>
                                                    </div>
                                                    <button className="mpv-sec-modal-submit" onClick={() => setAuthStep(2)}>Next →</button>
                                                </div>
                                            )}

                                            {authStep === 2 && (
                                                <div className="mpv-auth-step">
                                                    <p className="mpv-sec-modal-hint">Step 2: Scan the QR code or enter the secret key manually in the Google Authenticator app.</p>
                                                    <div className="mpv-auth-qr">
                                                        <img src={`https://api.qrserver.com/v1/create-qr-code/?size=180x180&data=otpauth://totp/Bicoin:${encodeURIComponent(userEmail)}?secret=${authSecret}%26issuer=Bicoin`} alt="QR Code" style={{ width: 180, height: 180, borderRadius: 8, background: '#fff', padding: 8 }} />
                                                    </div>
                                                    <div className="mpv-auth-secret">
                                                        <span className="mpv-auth-secret-label">Secret Key</span>
                                                        <div className="mpv-auth-secret-box">
                                                            <code>{authSecret}</code>
                                                            <Copy size={14} style={{ cursor: 'pointer', flexShrink: 0 }} onClick={() => copyToClipboard(authSecret)} />
                                                        </div>
                                                    </div>
                                                    <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
                                                        <button className="mpv-sec-modal-submit" style={{ background: 'var(--ui-bg-elevated)', flex: 1 }} onClick={() => setAuthStep(1)}>← Back</button>
                                                        <button className="mpv-sec-modal-submit" style={{ flex: 1 }} onClick={() => setAuthStep(3)}>Next →</button>
                                                    </div>
                                                </div>
                                            )}

                                            {authStep === 3 && (
                                                <div className="mpv-auth-step">
                                                    <p className="mpv-sec-modal-hint">Step 3: Enter the 6-digit verification code from the Google Authenticator app.</p>
                                                    <div className="mpv-sec-modal-field">
                                                        <label>Verification Code</label>
                                                        <input type="text" inputMode="numeric" maxLength={6} placeholder="Enter 6-digit code" value={secForm.otpCode || ''} onChange={e => setSecForm(p => ({ ...p, otpCode: e.target.value.replace(/\D/g, '').slice(0, 6) }))} style={{ letterSpacing: 8, textAlign: 'center', fontSize: 20, fontWeight: 600 }} />
                                                    </div>
                                                    <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
                                                        <button className="mpv-sec-modal-submit" style={{ background: 'var(--ui-bg-elevated)', flex: 1 }} onClick={() => setAuthStep(2)}>← Back</button>
                                                        <button className="mpv-sec-modal-submit" style={{ flex: 1 }} disabled={secLoading} onClick={handleBindAuthenticator}>{secLoading ? 'Verifying...' : 'Bind Authenticator'}</button>
                                                    </div>
                                                </div>
                                            )}
                                        </>
                                    )}

                                    {secModal === 'disable-2fa' && (
                                        <>
                                            <h3 className="mpv-sec-modal-title"><ShieldOff size={18} /> Disable 2FA</h3>
                                            <p className="mpv-sec-modal-hint">Enter your Google Authenticator code to unbind 2FA</p>
                                            <div className="mpv-sec-modal-field">
                                                <label>Verification Code</label>
                                                <input type="text" inputMode="numeric" maxLength={6} placeholder="6-digit code" value={secForm.otpCode || ''} onChange={e => setSecForm(p => ({ ...p, otpCode: e.target.value.replace(/\D/g, '').slice(0, 6) }))} style={{ letterSpacing: 8, textAlign: 'center', fontSize: 20, fontWeight: 600 }} />
                                            </div>
                                            <button className="mpv-sec-modal-submit danger" disabled={secLoading} onClick={handleDisable2FA}>{secLoading ? 'Unbinding...' : 'Disable Authenticator'}</button>
                                        </>
                                    )}

                                    {secModal === 'change-2fa' && (
                                        <>
                                            <h3 className="mpv-sec-modal-title"><RefreshCw size={18} /> Change 2FA</h3>
                                            {authStep === 1 && (
                                                <div className="mpv-auth-step">
                                                    <p className="mpv-sec-modal-hint">Step 1: Enter your current Google Authenticator code.</p>
                                                    <div className="mpv-sec-modal-field">
                                                        <label>Current Code</label>
                                                        <input type="text" inputMode="numeric" maxLength={6} placeholder="6-digit code" value={secForm.oldCode || ''} onChange={e => setSecForm(p => ({ ...p, oldCode: e.target.value.replace(/\D/g, '').slice(0, 6) }))} style={{ letterSpacing: 8, textAlign: 'center', fontSize: 20, fontWeight: 600 }} />
                                                    </div>
                                                    <button className="mpv-sec-modal-submit" disabled={!secForm.oldCode || secForm.oldCode.length !== 6} onClick={() => setAuthStep(2)}>Next →</button>
                                                </div>
                                            )}
                                            {authStep === 2 && (
                                                <div className="mpv-auth-step">
                                                    <p className="mpv-sec-modal-hint">Step 2: Scan the new QR code or enter the new secret key manually.</p>
                                                    <div className="mpv-auth-qr">
                                                        <img src={`https://api.qrserver.com/v1/create-qr-code/?size=180x180&data=otpauth://totp/Bicoin:${encodeURIComponent(userEmail)}?secret=${authSecret}%26issuer=Bicoin`} alt="QR Code" style={{ width: 180, height: 180, borderRadius: 8, background: '#fff', padding: 8 }} />
                                                    </div>
                                                    <div className="mpv-auth-secret">
                                                        <span className="mpv-auth-secret-label">New Secret Key</span>
                                                        <div className="mpv-auth-secret-box"><code>{authSecret}</code><Copy size={14} style={{ cursor: 'pointer', flexShrink: 0 }} onClick={() => copyToClipboard(authSecret)} /></div>
                                                    </div>
                                                    <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
                                                        <button className="mpv-sec-modal-submit" style={{ background: 'var(--ui-bg-elevated)', flex: 1 }} onClick={() => setAuthStep(1)}>← Back</button>
                                                        <button className="mpv-sec-modal-submit" style={{ flex: 1 }} onClick={() => setAuthStep(3)}>Next →</button>
                                                    </div>
                                                </div>
                                            )}
                                            {authStep === 3 && (
                                                <div className="mpv-auth-step">
                                                    <p className="mpv-sec-modal-hint">Step 3: Enter the new 6-digit code from Google Authenticator.</p>
                                                    <div className="mpv-sec-modal-field">
                                                        <label>New Verification Code</label>
                                                        <input type="text" inputMode="numeric" maxLength={6} placeholder="6-digit code" value={secForm.otpCode || ''} onChange={e => setSecForm(p => ({ ...p, otpCode: e.target.value.replace(/\D/g, '').slice(0, 6) }))} style={{ letterSpacing: 8, textAlign: 'center', fontSize: 20, fontWeight: 600 }} />
                                                    </div>
                                                    <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
                                                        <button className="mpv-sec-modal-submit" style={{ background: 'var(--ui-bg-elevated)', flex: 1 }} onClick={() => setAuthStep(2)}>← Back</button>
                                                        <button className="mpv-sec-modal-submit" style={{ flex: 1 }} disabled={secLoading} onClick={handleChange2FA}>{secLoading ? 'Verifying...' : 'Change Authenticator'}</button>
                                                    </div>
                                                </div>
                                            )}
                                        </>
                                    )}

                                    {secModal === 'fund-password' && (
                                        <>
                                            <h3 className="mpv-sec-modal-title"><ShieldCheck size={18} /> {profile?.hasFundPassword ? 'Change' : 'Set'} Fund Password</h3>
                                            <p className="mpv-sec-modal-hint">Login password verification is required</p>
                                            <div className="mpv-sec-modal-field"><label>Login Password</label><input type="password" placeholder="Enter login password" value={secForm.currentPassword || ''} onChange={e => setSecForm(p => ({ ...p, currentPassword: e.target.value }))} /></div>
                                            <div className="mpv-sec-modal-field"><label>New Fund Password</label><input type="password" placeholder="Enter fund password" value={secForm.newFundPassword || ''} onChange={e => setSecForm(p => ({ ...p, newFundPassword: e.target.value }))} /></div>
                                            <div className="mpv-sec-modal-field"><label>Confirm Fund Password</label><input type="password" placeholder="Confirm fund password" value={secForm.confirmFundPassword || ''} onChange={e => setSecForm(p => ({ ...p, confirmFundPassword: e.target.value }))} /></div>
                                            <button className="mpv-sec-modal-submit" disabled={secLoading} onClick={handleSetFundPassword}>{secLoading ? 'Saving...' : (profile?.hasFundPassword ? 'Change Fund Password' : 'Set Fund Password')}</button>
                                        </>
                                    )}

                                    {secError && <p className="mpv-sec-modal-error">{secError}</p>}
                                    {secSuccess && <p className="mpv-sec-modal-success">{secSuccess}</p>}
                                </div>
                            </div>
                        )}
                    </div>
                )}

                {/* Task Center Tab - Mobile */}

                {/* Invite Friends Tab - Mobile */}
                {activeProfileTab === 'invite' && (
                    <div className="mpv-content mpv-invite">
                        {/* Invite Header */}
                        <div className="mpv-invite-header">
                            <div className="mpv-invite-text">
                                <h2 className="mpv-invite-title">Invite friends</h2>
                                <h3 className="mpv-invite-subtitle">Enjoy Rewards</h3>
                                <p className="mpv-invite-desc">Enjoy rewarding, transparent and instant redemption</p>
                            </div>
                            <div className="mpv-invite-icon">
                                <UserPlus size={32} />
                            </div>
                        </div>

                        {/* Invitation Code */}
                        <div className="mpv-invite-section">
                            <span className="mpv-invite-label">Invitation code</span>
                            <div className="mpv-invite-box">
                                <span>{referralCode}</span>
                                <div className="mpv-invite-actions">
                                    <span>✏️</span>
                                    <Copy size={14} style={{ cursor: 'pointer' }} onClick={() => copyToClipboard(referralCode)} />
                                </div>
                            </div>
                        </div>

                        {/* Invitation Link */}
                        <div className="mpv-invite-section">
                            <span className="mpv-invite-label">Invitation link</span>
                            <div className="mpv-invite-box">
                                <span style={{ fontSize: 12, overflow: 'hidden', textOverflow: 'ellipsis' }}>{referralLink}</span>
                                <Copy size={14} style={{ cursor: 'pointer', flexShrink: 0 }} onClick={() => copyToClipboard(referralLink)} />
                            </div>
                        </div>

                        {/* Invite Button */}
                        <button className="mpv-invite-btn" onClick={() => {
                            if (navigator.share) navigator.share({ title: 'Join Bicoin', text: `Use my referral code: ${referralCode}`, url: referralLink }).catch(() => { })
                            else copyToClipboard(referralLink)
                        }}>Invite friends</button>

                        {/* Dashboard */}
                        <div className="mpv-invite-dashboard">
                            <div className="mpv-invite-dash-icon">
                                <Gift size={32} />
                            </div>
                            <h3>Dashboard</h3>
                        </div>

                        {/* Stats */}
                        <div className="mpv-invite-stats">
                            <div className="mpv-invite-stat">
                                <div className="mpv-invite-stat-icon">
                                    <Users size={18} />
                                </div>
                                <div className="mpv-invite-stat-info">
                                    <span className="mpv-invite-stat-label">Friends</span>
                                    <span className="mpv-invite-stat-value"><span className="teal">{referralFriendsCount}</span></span>
                                </div>
                            </div>
                            <div className="mpv-invite-stat">
                                <div className="mpv-invite-stat-icon teal">
                                    <Gift size={18} />
                                </div>
                                <div className="mpv-invite-stat-info">
                                    <span className="mpv-invite-stat-label">Rewards</span>
                                    <span className="mpv-invite-stat-value"><span className="teal">{referralTotalRewards.toFixed(2)}</span> USDT</span>
                                </div>
                            </div>
                            <div className="mpv-invite-stat">
                                <div className="mpv-invite-stat-icon teal">
                                    <Gift size={18} />
                                </div>
                                <div className="mpv-invite-stat-info">
                                    <span className="mpv-invite-stat-label">Top referral</span>
                                    <span className="mpv-invite-stat-value"><span className="teal">{referralTopRebate.toFixed(2)}</span> USDT</span>
                                </div>
                            </div>
                        </div>

                        {/* My Invitees — same fields as desktop (Invitee UID, Activation Date,
                            Transaction Amount, Transaction Fee, Rebate Ratio, Rebate Amount),
                            laid out as cards since a 6-column table doesn't fit mobile width */}
                        <div style={{ padding: '4px 16px 16px' }}>
                            <div style={{ fontSize: 14, fontWeight: 700, color: 'var(--ui-text-primary)', margin: '12px 0 10px' }}>My Invitees</div>
                            {referralStats?.length > 0 ? (
                                referralStats.map((ref: any, idx: number) => (
                                    <div key={idx} style={{ background: 'var(--ui-bg-card)', border: '1px solid var(--ui-border)', borderRadius: 12, padding: '14px 16px', marginBottom: 10 }}>
                                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
                                            <span style={{ fontSize: 14, fontWeight: 700, color: 'var(--ui-text-primary)' }}>UID {ref.uid}</span>
                                            <span style={{ fontSize: 12, color: 'var(--ui-text-muted)' }}>{new Date(ref.activationDate).toLocaleDateString()}</span>
                                        </div>
                                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, marginBottom: 8 }}>
                                            <div>
                                                <div style={{ fontSize: 11, color: 'var(--ui-text-muted)', marginBottom: 2 }}>Transaction Amount</div>
                                                <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--ui-text-primary)' }}>${Number(ref.transactionAmount).toFixed(2)}</div>
                                            </div>
                                            <div style={{ textAlign: 'right' }}>
                                                <div style={{ fontSize: 11, color: 'var(--ui-text-muted)', marginBottom: 2 }}>Transaction Fee</div>
                                                <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--ui-text-primary)' }}>${Number(ref.transactionFee).toFixed(2)}</div>
                                            </div>
                                        </div>
                                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', paddingTop: 8, borderTop: '1px solid var(--ui-border)' }}>
                                            <span style={{ fontSize: 12, color: 'var(--ui-text-muted)' }}>Rebate Ratio: <span style={{ color: 'var(--ui-text-primary)', fontWeight: 600 }}>{ref.rebateRatio}%</span></span>
                                            <span style={{ fontSize: 14, fontWeight: 700, color: '#1CD4A7' }}>+${Number(ref.rebateAmount).toFixed(2)}</span>
                                        </div>
                                    </div>
                                ))
                            ) : (
                                <div style={{ textAlign: 'center', padding: '32px 0', color: 'var(--ui-text-muted)', fontSize: 13 }}>
                                    No invitees yet. Start sharing your link!
                                </div>
                            )}
                        </div>
                    </div>
                )}

                {/* Settings Tab - Mobile */}
                {activeProfileTab === 'settings' && (
                    <div className="mpv-content mpv-settings">
                        {/* Profile Section */}
                        <div className="mpv-settings-section">
                            <h3 className="mpv-settings-title">Profile</h3>

                            <div className="mpv-settings-item">
                                <div className="mpv-settings-icon">
                                    <User size={18} />
                                </div>
                                <div className="mpv-settings-info">
                                    <span className="mpv-settings-name">Nickname</span>
                                    <span className="mpv-settings-desc">Set a customized nickname</span>
                                    <span className="mpv-settings-value">{userName}</span>
                                </div>
                                <button className="mpv-settings-btn" onClick={() => { setNicknameInput(userName === 'User' ? '' : userName); setNicknameSuccess(''); setShowNicknameModal(true) }}>Edit</button>
                            </div>

                            <div className="mpv-settings-item">
                                <div className="mpv-settings-icon">
                                    <User size={18} />
                                </div>
                                <div className="mpv-settings-info">
                                    <span className="mpv-settings-name">Avatar</span>
                                    <span className="mpv-settings-desc">Select an avatar to personalize your account</span>
                                </div>
                                <button className="mpv-settings-btn" onClick={handleAvatarClick}>Edit</button>
                            </div>
                        </div>

                        {/* Preference Section */}
                        <div className="mpv-settings-section">
                            <h3 className="mpv-settings-title">Preference</h3>

                            <div className="mpv-settings-item">
                                <div className="mpv-settings-icon">
                                    <FileText size={18} />
                                </div>
                                <div className="mpv-settings-info">
                                    <span className="mpv-settings-name">Order confirmation</span>
                                    <span className="mpv-settings-desc">If you enable the reminder, an order will need to be reconfirmed every time</span>
                                    <span className="mpv-settings-value">{[orderConfirmSettings.limit && 'Limit order', orderConfirmSettings.market && 'Market order', orderConfirmSettings.stop && 'Stop limit'].filter(Boolean).join(', ') || 'None'}</span>
                                </div>
                                <button className="mpv-settings-btn" onClick={() => setShowOrderConfirmModal(true)}>Manage</button>
                            </div>

                            <div className="mpv-settings-item">
                                <div className="mpv-settings-icon">
                                    <UserPlus size={18} />
                                </div>
                                <div className="mpv-settings-info">
                                    <span className="mpv-settings-name">Add superiors invitation code</span>
                                    <span className="mpv-settings-desc">Used for invitation relationship</span>
                                    <span className="mpv-settings-link" onClick={() => { setInviteCodeInput(savedInviteCode); setInviteCodeSuccess(''); setShowInviteCodeModal(true) }}>{savedInviteCode || 'Add'}</span>
                                </div>
                                <button className="mpv-settings-btn" onClick={() => { setInviteCodeInput(savedInviteCode); setInviteCodeSuccess(''); setShowInviteCodeModal(true) }}>{savedInviteCode ? 'Edit' : 'Add'}</button>
                            </div>
                        </div>
                    </div>
                )}

                {/* Order Tab - Mobile */}

                {/* Message Tab - Mobile */}
            </div>

            {/* Desktop View */}
            <div className="profile-page-layout desktop-only">
                {/* Profile Sidebar */}
                <div className="profile-sidebar">
                    <button
                        className={`sidebar-item ${activeProfileTab === 'overview' ? 'active' : ''}`}
                        onClick={() => setActiveProfileTab('overview')}
                    >
                        <User size={20} />
                        <span>Overview</span>
                    </button>
                    <button
                        className={`sidebar-item ${activeProfileTab === 'verification' ? 'active' : ''}`}
                        onClick={() => setActiveProfileTab('verification')}
                    >
                        <ShieldCheck size={20} />
                        <span>Verification</span>
                    </button>
                    <button
                        className={`sidebar-item ${activeProfileTab === 'security' ? 'active' : ''}`}
                        onClick={() => setActiveProfileTab('security')}
                    >
                        <Lock size={20} />
                        <span>Security</span>
                    </button>
                    <button
                        className="sidebar-item"
                        onClick={() => navigate('/dashboard/referral')}
                    >
                        <UserPlus size={20} />
                        <span>Invite friends</span>
                    </button>
                    <button
                        className={`sidebar-item ${activeProfileTab === 'settings' ? 'active' : ''}`}
                        onClick={() => setActiveProfileTab('settings')}
                    >
                        <Settings size={20} />
                        <span>Settings</span>
                    </button>
                </div>

                <div className="profile-main-content">
                    {activeProfileTab === 'overview' && (
                        <>
                        <div className="profile-header-card">
                            <div className="profile-info-left">
                                <div className="profile-avatar-circle" onClick={handleAvatarClick} style={{ cursor: 'pointer', position: 'relative' }}>
                                    <img src={avatarPreview || '/avatar.webp'} alt={userName} className="profile-avatar-img" />
                                    <div style={{ position: 'absolute', bottom: 2, right: 2, background: '#1CD4A7', borderRadius: '50%', width: 28, height: 28, display: 'flex', alignItems: 'center', justifyContent: 'center', border: '2px solid var(--ui-bg-primary)' }}>
                                        <Camera size={14} color="#fff" />
                                    </div>
                                    {avatarUploading && <div style={{ position: 'absolute', inset: 0, background: 'rgba(0,0,0,0.5)', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--ui-text-primary)', fontSize: 12 }}>Uploading...</div>}
                                </div>
                                <div className="profile-details">
                                    <h1 className="profile-name">{userName}</h1>
                                    <p className="profile-email">{userEmail}</p>
                                </div>
                            </div>
                            <div className="profile-info-right">
                                <div className="info-item">
                                    <span className="info-label">UID</span>
                                    <div className="info-value-container">
                                        <span>{profile?.uid ? String(profile.uid) : '--------'}</span>
                                        <button className="copy-icon-btn">
                                            <Copy size={14} />
                                        </button>
                                    </div>
                                </div>
                                <div className="info-item">
                                    <span className="info-label">ID Verification</span>
                                    <div className="verification-status" onClick={() => navigate('/dashboard/kyc')} style={{ cursor: 'pointer' }}>
                                        <span className="not-verified-text">{kycStatus === 'verified' ? 'Verified' : kycStatus === 'pending' ? 'Pending' : 'Not verified'}</span>
                                        <ChevronRight size={16} className="chevron-right-icon" />
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Verification Banner - only when not verified */}
                        {kycStatus !== 'verified' && (
                        <div className="verification-banner">
                            <div className="banner-left">
                                <div className="banner-icon-bg">
                                    <img src="/verification-icon.webp" alt="Verification" className="banner-icon-img" />
                                </div>
                                <div className="banner-text-content">
                                    <h3 className="banner-title">Pending verification</h3>
                                    <p className="banner-desc">
                                        You will enjoy the full benefit of the whole service after the completion of the verification
                                    </p>
                                </div>
                            </div>
                            <button className="view-details-btn" onClick={() => navigate('/dashboard/kyc')}>View details</button>
                        </div>
                        )}

                        {/* Balance Overview Card */}
                        <div className="balance-overview-card">
                            <div className="balance-info">
                                <div className="estimated-value-label">
                                    <span>Estimated value</span>
                                    <button className="eye-icon-btn" onClick={() => setBalanceHidden(h => !h)}>
                                        {balanceHidden ? <EyeOff size={16} /> : <Eye size={16} />}
                                    </button>
                                </div>
                                <div className="balance-amount-display">
                                    <div className="btc-amount-row">
                                        <span className="btc-value">{balanceHidden ? '******' : `${userBalance.toFixed(2)} USDT`}</span>
                                    </div>
                                    <span className="usd-equivalent">{balanceHidden ? '****' : `≈$${userBalance.toFixed(2)}`}</span>
                                </div>
                            </div>
                            <div className="balance-actions">
                                <button className="btn-deposit" onClick={() => navigate('/dashboard/deposit')}>Deposit</button>
                                <button className="btn-withdraw" onClick={() => navigate('/dashboard/deposit')}>Withdraw</button>
                            </div>
                        </div>

                        {/* Assets Table Section */}
                        <div className="assets-section-card">
                            <table className="assets-table">
                                <thead>
                                    <tr>
                                        <th>Coin</th>
                                        <th>Total Balance</th>
                                        <th>Wallet Balance</th>
                                        <th>Available</th>
                                        <th>PnL</th>
                                        <th>Operation</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {assets.map((asset) => (
                                        <tr key={asset.symbol}>
                                            <td>
                                                <div className="asset-coin-cell">
                                                    <div className="asset-icon-placeholder" style={{ background: 'transparent', padding: 0 }}>
                                                        <img src={asset.symbol === 'BTC' ? 'https://assets.coingecko.com/coins/images/1/small/bitcoin.png' : 'https://assets.coingecko.com/coins/images/279/small/ethereum.png'} alt={asset.symbol} style={{ width: 28, height: 28, borderRadius: '50%' }} />
                                                    </div>
                                                    <div className="asset-name-info">
                                                        <span className="asset-symbol">{asset.symbol}</span>
                                                        <span className="asset-full-name">{asset.name}</span>
                                                    </div>
                                                </div>
                                            </td>
                                            <td>
                                                <div className="asset-balance-cell">
                                                    <div>{asset.totalBalance}</div>
                                                    <div className="balance-secondary">{asset.usdValue}</div>
                                                </div>
                                            </td>
                                            <td>{asset.walletBalance}</td>
                                            <td>{asset.available}</td>
                                            <td>{asset.pnl}</td>
                                            <td>
                                                <a href="#" className="trade-link-teal" onClick={(e) => { e.preventDefault(); navigate(`/dashboard/trade?pair=${asset.symbol}/USDT`); }}>Trade</a>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </>
                )}

                {/* Verification Tab Content */}
                {activeProfileTab === 'verification' && (
                    <div className="profile-tab-content">
                        {/* User Info Card */}
                        <div className="verification-user-card">
                            <div className="verification-user-info">
                                <div className="verification-avatar">
                                    <User size={24} />
                                </div>
                                <div className="verification-user-details">
                                    <span className="verification-email">{userEmail}</span>
                                    <span className={`verification-status-badge ${kycStatus === 'verified' ? 'verified' : ''}`}>{kycStatus === 'verified' ? 'Verified' : kycStatus === 'pending' ? 'Pending' : 'Unverified'}</span>
                                </div>
                            </div>
                        </div>

                        {/* Verification Section */}
                        <div className="verification-section-card">
                            <div className="verification-section-left">
                                <h3 className="verification-section-title">User-verification</h3>
                                <p className="verification-section-desc">Only takes 3-5 minutes to complete the identity verification to protect your account from fraud and illegal risks.</p>
                                {kycStatus === 'verified' ? (
                                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, color: '#1CD4A7', fontWeight: 600, padding: '8px 0' }}><CheckSquare size={16} /> Verified</div>
                                ) : kycStatus === 'pending' ? (
                                    <div style={{ color: '#F59E0B', fontWeight: 600, padding: '8px 0' }}>Verification in progress...</div>
                                ) : (
                                    <button className="verification-btn" onClick={() => navigate('/dashboard/kyc')}>Verify</button>
                                )}
                            </div>
                            <div className="verification-section-right">
                                <div className="verification-level">
                                    <div className="level-header">
                                        <CheckSquare size={16} className="level-icon success" />
                                        <span>User-Verification</span>
                                    </div>
                                    <p className="level-limit">Crypto withdrawal limit 2000 USDT Daily</p>
                                    <div className="level-requirements">
                                        <span className="level-label">Require</span>
                                        <div className="requirement-item">
                                            <span className="req-dot"></span>
                                            <span>Applicant data</span>
                                        </div>
                                        <div className="requirement-item">
                                            <span className="req-dot"></span>
                                            <span>Identity document</span>
                                        </div>
                                        <div className="requirement-item">
                                            <span className="req-dot"></span>
                                            <span>Selfie with document</span>
                                        </div>
                                    </div>
                                </div>
                                <div className="verification-level">
                                    <div className="level-header">
                                        <X size={16} className="level-icon error" />
                                        <span>Additional proof of address</span>
                                    </div>
                                    <p className="level-limit">Crypto withdrawal limit 20000 USDT Daily</p>
                                    <div className="level-requirements">
                                        <span className="level-label">Require</span>
                                        <div className="requirement-item">
                                            <span className="req-dot"></span>
                                            <span>Identity document</span>
                                        </div>
                                        <div className="requirement-item">
                                            <span className="req-dot"></span>
                                            <span>Proof of residence</span>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Account Limits */}
                        <div className="account-limits-card">
                            <div className="limits-header">
                                <Lock size={16} />
                                <span>Account limits</span>
                            </div>
                            <div className="limits-grid">
                                <div className="limit-row">
                                    <span className="limit-label">Crypto deposit limit</span>
                                    <span className="limit-value">Enable deposit</span>
                                </div>
                                <div className="limit-row">
                                    <span className="limit-label">Crypto withdrawal limit</span>
                                    <div className="limit-values">
                                        <span>20000 USDT Daily</span>
                                        <span>20000 USDT Monthly</span>
                                    </div>
                                </div>
                                <div className="limit-row">
                                    <span className="limit-label">P2p transaction limit</span>
                                    <span className="limit-value">Enable transaction</span>
                                </div>
                            </div>
                        </div>
                    </div>
                )}

                {/* Security Tab Content */}
                {activeProfileTab === 'security' && (
                    <div className="profile-tab-content">
                        {/* Authentication Method */}
                        <div className="security-section">
                            <h3 className="security-section-title">Authentication method</h3>
                            <div className="security-card">
                                <div className="security-item">
                                    <div className="security-item-icon">
                                        <Shield size={18} />
                                    </div>
                                    <div className="security-item-info">
                                        <span className="security-item-name">Google Authenticator</span>
                                        <span className="security-item-desc">API Secure verification when withdrawing, retrieving passwords, modifying security settings and managing API</span>
                                    </div>
                                    {profile?.isGoogleAuthenticatorEnabled ? (
                                        <div style={{ display: 'flex', gap: 8 }}>
                                            <button className="security-btn" onClick={() => {
                                                setSecForm({ otpCode: '' });
                                                setSecModal('disable-2fa');
                                            }}>Unbind</button>
                                            <button className="security-btn" onClick={() => {
                                                setSecForm({ oldCode: '', otpCode: '' });
                                                setAuthStep(1);
                                                generateNewSecret();
                                                setSecModal('change-2fa');
                                            }}>Change</button>
                                        </div>
                                    ) : (
                                        <button className="security-btn" onClick={() => {
                                            setSecForm({});
                                            setAuthStep(1);
                                            generateNewSecret();
                                            setSecModal('authenticator');
                                        }}>Bind</button>
                                    )}
                                </div>
                                <div className="security-item">
                                    <div className="security-item-icon">
                                        <Smartphone size={18} />
                                    </div>
                                    <div className="security-item-info">
                                        <span className="security-item-name">Phone number</span>
                                        <span className="security-item-desc">Receive verification SMS that is used to withdraw, change the password or security settings</span>
                                    </div>
                                    <span className="security-item-value">{userPhone}</span>
                                    <button className="security-btn" onClick={() => { setSecForm({ phone: '' }); setSecModal('phone'); }}>{(profile as any)?.phoneNumber && (profile as any)?.phoneNumber !== 'Not set' ? 'Change' : 'Bind'}</button>
                                </div>
                                <div className="security-item">
                                    <div className="security-item-icon">
                                        <Mail size={18} />
                                    </div>
                                    <div className="security-item-info">
                                        <span className="security-item-name">Email address</span>
                                        <span className="security-item-desc">Used when logging in, withdrawing and modifying security settings</span>
                                    </div>
                                    <span className="security-item-value">{userEmail}</span>
                                    <button className="security-btn" onClick={() => { setSecForm({ newEmail: '', password: '' }); setSecModal('email'); }}>Change</button>
                                </div>
                            </div>
                        </div>

                        {/* Advanced Security */}
                        <div className="security-section">
                            <h3 className="security-section-title">Advanced Security</h3>
                            <div className="security-card">
                                <div className="security-item">
                                    <div className="security-item-icon">
                                        <Lock size={18} />
                                    </div>
                                    <div className="security-item-info">
                                        <span className="security-item-name">Password</span>
                                        <span className="security-item-desc">Used to manage your account login password</span>
                                    </div>
                                    <button className="security-btn" onClick={() => { setSecForm({ currentPassword: '', newPassword: '', confirmPassword: '' }); setSecModal('password'); }}>Change</button>
                                </div>
                                <div className="security-item">
                                    <div className="security-item-icon">
                                        <Wallet size={18} />
                                    </div>
                                    <div className="security-item-info">
                                        <span className="security-item-name">Address management</span>
                                        <span className="security-item-desc">After setting as a trust address, withdrawals will be exempt from security verification</span>
                                    </div>
                                    <button className="security-btn" onClick={() => navigate('/dashboard/deposit')}>Manage</button>
                                </div>
                                <div className="security-item">
                                    <div className="security-item-icon">
                                        <ShieldCheck size={18} />
                                    </div>
                                    <div className="security-item-info">
                                        <span className="security-item-name">Fund Password</span>
                                        <span className="security-item-desc">Set a password for withdrawals to increase fund security</span>
                                    </div>
                                    <button className="security-btn" onClick={() => { setSecForm({ currentPassword: '', newFundPassword: '', confirmFundPassword: '' }); setSecModal('fund-password'); }}>{profile?.hasFundPassword ? 'Change' : 'Set'}</button>
                                </div>
                            </div>
                        </div>

                        {/* Account Management */}
                        <div className="security-section">
                            <h3 className="security-section-title">Account Management</h3>
                            <div className="security-card">
                                <div className="security-item">
                                    <div className="security-item-icon">
                                        <Monitor size={18} />
                                    </div>
                                    <div className="security-item-info">
                                        <span className="security-item-name">My device</span>
                                        <span className="security-item-desc">For managing logged-in devices and viewing device history</span>
                                    </div>
                                    <button className="security-btn" onClick={() => setSecModal('device')}>Manage</button>
                                </div>
                                <div className="security-item">
                                    <div className="security-item-icon">
                                        <Activity size={18} />
                                    </div>
                                    <div className="security-item-info">
                                        <span className="security-item-name">Account activity</span>
                                        <span className="security-item-desc">Last login: {profile?.lastLogin ? new Date(profile.lastLogin).toLocaleString() : 'N/A'}</span>
                                    </div>
                                    <button className="security-btn" onClick={() => setSecModal('activity')}>Manage</button>
                                </div>
                                <div className="security-item">
                                    <div className="security-item-icon">
                                        <Trash2 size={18} />
                                    </div>
                                    <div className="security-item-info">
                                        <span className="security-item-name">Delete account</span>
                                        <span className="security-item-desc">After deleting your account, you will never be able to re-register this account and its sub-account email, mobile phone number, and identity information.</span>
                                    </div>
                                    <button className="security-btn danger" onClick={() => { setSecForm({ password: '', confirmDelete: '' }); setSecModal('delete'); }}>Delete</button>
                                </div>
                            </div>
                        </div>
                    </div>
                )}

                {/* Task Center Tab Content */}

                {/* Invite Friends Tab Content */}
                {activeProfileTab === 'invite' && (
                    <div className="profile-tab-content">
                        {/* Invite Header Card */}
                        <div className="invite-header-card">
                            <div className="invite-header-left">
                                <h2 className="invite-title">Invite friends</h2>
                                <h3 className="invite-subtitle">Enjoy Rewards</h3>
                                <p className="invite-desc">Enjoy rewarding, transparent and instant redemption</p>
                            </div>
                            <div className="invite-header-icon">
                                <div className="invite-icon-box">
                                    <UserPlus size={32} />
                                </div>
                            </div>
                            <div className="invite-header-right">
                                <div className="invite-code-section">
                                    <span className="invite-label">Invitation code</span>
                                    <div className="invite-code-box">
                                        <span>{profile?.invitationCode || referralCode}</span>
                                        <div className="invite-code-actions">
                                            <button className="invite-edit-btn" onClick={() => copyToClipboard(referralCode)}>📋</button>
                                            <Copy size={14} style={{ cursor: 'pointer' }} onClick={() => copyToClipboard(referralCode)} />
                                        </div>
                                    </div>
                                </div>
                                <div className="invite-link-section">
                                    <span className="invite-label">Invitation link</span>
                                    <div className="invite-code-box">
                                        <span style={{ fontSize: '11px', wordBreak: 'break-all' }}>{referralLink}</span>
                                        <Copy size={14} style={{ cursor: 'pointer' }} onClick={() => copyToClipboard(referralLink)} />
                                    </div>
                                </div>
                                <button className="invite-btn" onClick={() => {
                                    if (navigator.share) {
                                        navigator.share({ title: 'Join Bicoin', text: `Use my referral code ${referralCode} to sign up!`, url: referralLink });
                                    } else {
                                        copyToClipboard(referralLink);
                                    }
                                }}>Invite friends</button>
                            </div>
                        </div>

                        {/* Dashboard Section */}
                        <div className="invite-dashboard">
                            <div className="invite-dashboard-icon">
                                <Gift size={32} />
                            </div>
                            <h3 className="invite-dashboard-title">Dashboard</h3>
                        </div>

                        {/* Stats Card */}
                        <div className="invite-stats-card">
                            <div className="invite-stat-item">
                                <div className="invite-stat-icon">
                                    <Users size={18} />
                                </div>
                                <div className="invite-stat-info">
                                    <span className="invite-stat-label">Friends</span>
                                    <span className="invite-stat-value"><span className="stat-amount">{referralFriendsCount}</span> People</span>
                                </div>
                            </div>
                            <div className="invite-stat-item">
                                <div className="invite-stat-icon teal">
                                    <Gift size={18} />
                                </div>
                                <div className="invite-stat-info">
                                    <span className="invite-stat-label">Rewards</span>
                                    <span className="invite-stat-value"><span className="stat-amount">{referralTotalRewards.toFixed(2)}</span> USDT</span>
                                </div>
                            </div>
                            <div className="invite-stat-item">
                                <div className="invite-stat-icon teal">
                                    <Gift size={18} />
                                </div>
                                <div className="invite-stat-info">
                                    <span className="invite-stat-label">Top referral</span>
                                    <span className="invite-stat-value"><span className="stat-amount">{referralTopRebate.toFixed(2)}</span> USDT</span>
                                </div>
                            </div>
                        </div>

                        {/* Referral List Section */}
                        <div className="referral-list-card">
                            <h3 className="referral-list-title">My Invitees</h3>
                            <div style={{ overflowX: 'auto' }}>
                                <table className="referral-table" style={{ minWidth: '800px' }}>
                                    <thead>
                                        <tr>
                                            <th>Invitee UID</th>
                                            <th>Activation Date</th>
                                            <th>Transaction Amount</th>
                                            <th>Transaction Fee</th>
                                            <th>Rebate Ratio</th>
                                            <th>Rebate Amount</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {referralStats?.length > 0 ? (
                                            referralStats.map((ref: any, idx: number) => (
                                                <tr key={idx}>
                                                    <td>{ref.uid}</td>
                                                    <td>{new Date(ref.activationDate).toLocaleDateString()}</td>
                                                    <td>${Number(ref.transactionAmount).toFixed(2)}</td>
                                                    <td>${Number(ref.transactionFee).toFixed(2)}</td>
                                                    <td>{ref.rebateRatio}%</td>
                                                    <td style={{ color: '#1CD4A7', fontWeight: 'bold' }}>${Number(ref.rebateAmount).toFixed(2)}</td>
                                                </tr>
                                            ))
                                        ) : (
                                            <tr>
                                                <td colSpan={6} style={{ textAlign: 'center', padding: '40px 0', color: 'rgba(255,255,255,0.4)' }}>
                                                    No invitees yet. Start sharing your link!
                                                </td>
                                            </tr>
                                        )}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    </div>
                )}

                {/* Settings Tab Content */}
                {activeProfileTab === 'settings' && (
                    <div className="profile-tab-content">
                        {/* Profile Section */}
                        <div className="settings-section">
                            <h3 className="settings-section-title">Profile</h3>
                            <div className="settings-card">
                                <div className="settings-item">
                                    <div className="settings-item-icon">
                                        <User size={18} />
                                    </div>
                                    <div className="settings-item-info">
                                        <span className="settings-item-name">Nickname</span>
                                        <span className="settings-item-desc">Set a customized nickname</span>
                                        <span className="settings-item-value-sub">{userName}</span>
                                    </div>
                                    <button className="settings-btn" onClick={() => { setNicknameInput(userName === 'User' ? '' : userName); setNicknameSuccess(''); setShowNicknameModal(true) }}>Edit</button>
                                </div>
                                <div className="settings-item">
                                    <div className="settings-item-icon">
                                        <User size={18} />
                                    </div>
                                    <div className="settings-item-info">
                                        <span className="settings-item-name">Avatar</span>
                                        <span className="settings-item-desc">Select an avatar to personalize your account</span>
                                    </div>
                                    <button className="settings-btn" onClick={handleAvatarClick}>Edit</button>
                                </div>
                            </div>
                        </div>

                        {/* Preference Section */}
                        <div className="settings-section">
                            <h3 className="settings-section-title">Preference</h3>
                            <div className="settings-card">
                                <div className="settings-item">
                                    <div className="settings-item-icon">
                                        <FileText size={18} />
                                    </div>
                                    <div className="settings-item-info">
                                        <span className="settings-item-name">Order confirmation</span>
                                        <span className="settings-item-desc">If you enable the reminder, an order will need to be reconfirmed every time</span>
                                        <span className="settings-item-value-sub">{[orderConfirmSettings.limit && 'Limit order', orderConfirmSettings.market && 'Market order', orderConfirmSettings.stop && 'Stop limit'].filter(Boolean).join(', ') || 'None'}</span>
                                    </div>
                                    <button className="settings-btn" onClick={() => setShowOrderConfirmModal(true)}>Manage</button>
                                </div>
                                <div className="settings-item">
                                    <div className="settings-item-icon">
                                        <UserPlus size={18} />
                                    </div>
                                    <div className="settings-item-info">
                                        <span className="settings-item-name">Add superiors invitation code</span>
                                        <span className="settings-item-desc">Used for invitation relationship</span>
                                        <span className="settings-item-value-link" style={{ cursor: 'pointer' }} onClick={() => { setInviteCodeInput(savedInviteCode); setInviteCodeSuccess(''); setShowInviteCodeModal(true) }}>{savedInviteCode || 'Add'}</span>
                                    </div>
                                    <button className="settings-btn" onClick={() => { setInviteCodeInput(savedInviteCode); setInviteCodeSuccess(''); setShowInviteCodeModal(true) }}>{savedInviteCode ? 'Edit' : 'Add'}</button>
                                </div>
                            </div>
                        </div>
                    </div>
                )}

            </div>
        </div>

            {/* Global Security Modal Overlay */}
            {secModal !== 'none' && (
                <div className="mpv-sec-modal-overlay" onClick={closeSecModal}>
                    <div className="mpv-sec-modal" onClick={e => e.stopPropagation()}>
                        <button className="mpv-sec-modal-close" onClick={closeSecModal}><X size={18} /></button>

                        {secModal === 'password' && (
                            <>
                                <h3 className="mpv-sec-modal-title"><Lock size={18} /> Change Password</h3>
                                <div className="mpv-sec-modal-field"><label>Current Password</label><input type="password" placeholder="Enter current password" value={secForm.currentPassword || ''} onChange={e => setSecForm(p => ({ ...p, currentPassword: e.target.value }))} /></div>
                                <div className="mpv-sec-modal-field"><label>New Password</label><input type="password" placeholder="Enter new password" value={secForm.newPassword || ''} onChange={e => setSecForm(p => ({ ...p, newPassword: e.target.value }))} /></div>
                                <div className="mpv-sec-modal-field"><label>Confirm New Password</label><input type="password" placeholder="Confirm new password" value={secForm.confirmPassword || ''} onChange={e => setSecForm(p => ({ ...p, confirmPassword: e.target.value }))} /></div>
                                <button className="mpv-sec-modal-submit" disabled={secLoading} onClick={handleChangePassword}>{secLoading ? 'Saving...' : 'Change Password'}</button>
                            </>
                        )}
                        {secModal === 'email' && (
                            <>
                                <h3 className="mpv-sec-modal-title"><Mail size={18} /> Change Email</h3>
                                <p className="mpv-sec-modal-hint">Current: {userEmail}</p>
                                <div className="mpv-sec-modal-field"><label>New Email</label><input type="email" placeholder="Enter new email" value={secForm.newEmail || ''} onChange={e => setSecForm(p => ({ ...p, newEmail: e.target.value }))} /></div>
                                <div className="mpv-sec-modal-field"><label>Password</label><input type="password" placeholder="Enter your password" value={secForm.password || ''} onChange={e => setSecForm(p => ({ ...p, password: e.target.value }))} /></div>
                                <button className="mpv-sec-modal-submit" disabled={secLoading} onClick={handleChangeEmail}>{secLoading ? 'Saving...' : 'Change Email'}</button>
                            </>
                        )}
                        {secModal === 'phone' && (
                            <>
                                <h3 className="mpv-sec-modal-title"><Smartphone size={18} /> {userPhone !== 'Not set' ? 'Change' : 'Bind'} Phone</h3>
                                {userPhone !== 'Not set' && <p className="mpv-sec-modal-hint">Current: {userPhone}</p>}
                                <div className="mpv-sec-modal-field"><label>Phone Number</label><input type="tel" placeholder="Enter phone number" value={secForm.phone || ''} onChange={e => setSecForm(p => ({ ...p, phone: e.target.value }))} /></div>
                                <button className="mpv-sec-modal-submit" disabled={secLoading} onClick={handleBindPhone}>{secLoading ? 'Saving...' : 'Save Phone'}</button>
                            </>
                        )}
                        {secModal === 'delete' && (
                            <>
                                <h3 className="mpv-sec-modal-title" style={{ color: '#ef4444' }}><AlertTriangle size={18} /> Delete Account</h3>
                                <p className="mpv-sec-modal-hint" style={{ color: '#ef4444' }}>This action is permanent and cannot be undone.</p>
                                <div className="mpv-sec-modal-field"><label>Password</label><input type="password" placeholder="Enter your password" value={secForm.password || ''} onChange={e => setSecForm(p => ({ ...p, password: e.target.value }))} /></div>
                                <div className="mpv-sec-modal-field"><label>Type DELETE to confirm</label><input type="text" placeholder="DELETE" value={secForm.confirmDelete || ''} onChange={e => setSecForm(p => ({ ...p, confirmDelete: e.target.value }))} /></div>
                                <button className="mpv-sec-modal-submit danger" disabled={secLoading} onClick={handleDeleteAccount}>{secLoading ? 'Deleting...' : 'Delete My Account'}</button>
                            </>
                        )}
                        {secModal === 'device' && (
                            <>
                                <h3 className="mpv-sec-modal-title"><Monitor size={18} /> My Devices</h3>
                                <div className="mpv-sec-device-list" style={{ maxHeight: 350, overflowY: 'auto' }}>
                                    {deviceSessions.length > 0 ? (
                                        deviceSessions.map((session) => (
                                            <div key={session._id} className={`mpv-sec-device-item ${session.isCurrent ? 'active' : ''}`} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '12px 0', borderBottom: '1px solid #2a2a3a' }}>
                                                <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                                                    <Monitor size={20} />
                                                    <div>
                                                        <span className="mpv-sec-device-name" style={{ display: 'block', color: 'var(--ui-text-primary)', fontSize: 14, fontWeight: 500 }}>
                                                            {session.deviceType || 'Unknown Device'}
                                                        </span>
                                                        <span className="mpv-sec-device-detail" style={{ display: 'block', color: 'var(--ui-text-muted)', fontSize: 12, marginTop: 2 }}>
                                                            {session.location || 'Unknown Location'} · {session.ipAddress}
                                                        </span>
                                                        <span style={{ display: 'block', color: 'var(--ui-text-muted)', fontSize: 11, marginTop: 2 }}>
                                                            Last active: {new Date(session.lastActive).toLocaleString()}
                                                        </span>
                                                    </div>
                                                </div>
                                                {session.isCurrent ? (
                                                    <span className="mpv-sec-device-badge" style={{ background: 'rgba(28,212,167,0.1)', color: '#1CD4A7', padding: '2px 8px', borderRadius: 4, fontSize: 12 }}>Active</span>
                                                ) : (
                                                    <button
                                                        onClick={() => handleRevokeDevice(session.tokenHash)}
                                                        style={{ background: '#ef4444', color: 'var(--ui-text-primary)', border: 'none', padding: '4px 10px', borderRadius: 4, cursor: 'pointer', fontSize: 12 }}
                                                    >
                                                        Logout
                                                    </button>
                                                )}
                                            </div>
                                        ))
                                    ) : (
                                        <p style={{ color: 'var(--ui-text-muted)', fontSize: 13, textAlign: 'center', padding: '20px 0' }}>No active devices found</p>
                                    )}
                                </div>
                            </>
                        )}
                        {secModal === 'activity' && (
                            <>
                                <h3 className="mpv-sec-modal-title"><Activity size={18} /> Account Activity</h3>
                                <div className="mpv-sec-activity-list">
                                    <div className="mpv-sec-activity-item">
                                        <span className="mpv-sec-activity-action">Login</span>
                                        <span className="mpv-sec-activity-time">{profile?.lastLogin ? new Date(profile.lastLogin).toLocaleString() : 'N/A'}</span>
                                        <span className="mpv-sec-activity-detail">{navigator.platform} · {navigator.userAgent.includes('Mobile') ? 'Mobile' : 'Desktop'}</span>
                                    </div>
                                    <div className="mpv-sec-activity-item">
                                        <span className="mpv-sec-activity-action">Account Created</span>
                                        <span className="mpv-sec-activity-time">{profile?.createdAt ? new Date(profile.createdAt).toLocaleString() : 'N/A'}</span>
                                    </div>
                                </div>
                            </>
                        )}
                        {secModal === 'authenticator' && (
                            <>
                                <h3 className="mpv-sec-modal-title"><Shield size={18} /> Google Authenticator</h3>
                                {authStep === 1 && (
                                    <div className="mpv-auth-step">
                                        <p className="mpv-sec-modal-hint">Step 1: Download Google Authenticator app from your App Store or Play Store.</p>
                                        <div className="mpv-auth-stores"><div className="mpv-auth-store-badge">📱 App Store</div><div className="mpv-auth-store-badge">📱 Play Store</div></div>
                                        <button className="mpv-sec-modal-submit" onClick={() => setAuthStep(2)}>Next →</button>
                                    </div>
                                )}
                                {authStep === 2 && (
                                    <div className="mpv-auth-step">
                                        <p className="mpv-sec-modal-hint">Step 2: Scan the QR code or enter the secret key manually.</p>
                                        <div className="mpv-auth-qr">
                                            <img src={`https://api.qrserver.com/v1/create-qr-code/?size=180x180&data=otpauth://totp/Bicoin:${encodeURIComponent(userEmail)}?secret=${authSecret}%26issuer=Bicoin`} alt="QR Code" style={{ width: 180, height: 180, borderRadius: 8, background: '#fff', padding: 8 }} />
                                        </div>
                                        <div className="mpv-auth-secret">
                                            <span className="mpv-auth-secret-label">Secret Key</span>
                                            <div className="mpv-auth-secret-box"><code>{authSecret}</code><Copy size={14} style={{ cursor: 'pointer', flexShrink: 0 }} onClick={() => copyToClipboard(authSecret)} /></div>
                                        </div>
                                        <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
                                            <button className="mpv-sec-modal-submit" style={{ background: 'var(--ui-bg-elevated)', flex: 1 }} onClick={() => setAuthStep(1)}>← Back</button>
                                            <button className="mpv-sec-modal-submit" style={{ flex: 1 }} onClick={() => setAuthStep(3)}>Next →</button>
                                        </div>
                                    </div>
                                )}
                                {authStep === 3 && (
                                    <div className="mpv-auth-step">
                                        <p className="mpv-sec-modal-hint">Step 3: Enter the 6-digit verification code from Google Authenticator.</p>
                                        <div className="mpv-sec-modal-field">
                                            <label>Verification Code</label>
                                            <input type="text" inputMode="numeric" maxLength={6} placeholder="Enter 6-digit code" value={secForm.otpCode || ''} onChange={e => setSecForm(p => ({ ...p, otpCode: e.target.value.replace(/\D/g, '').slice(0, 6) }))} style={{ letterSpacing: 8, textAlign: 'center', fontSize: 20, fontWeight: 600 }} />
                                        </div>
                                        <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
                                            <button className="mpv-sec-modal-submit" style={{ background: 'var(--ui-bg-elevated)', flex: 1 }} onClick={() => setAuthStep(2)}>← Back</button>
                                            <button className="mpv-sec-modal-submit" style={{ flex: 1 }} disabled={secLoading} onClick={handleBindAuthenticator}>{secLoading ? 'Verifying...' : 'Bind Authenticator'}</button>
                                        </div>
                                    </div>
                                )}
                            </>
                        )}
                        {secModal === 'disable-2fa' && (
                            <>
                                <h3 className="mpv-sec-modal-title"><ShieldOff size={18} /> Disable 2FA</h3>
                                <p className="mpv-sec-modal-hint">Enter your Google Authenticator code to unbind 2FA</p>
                                <div className="mpv-sec-modal-field">
                                    <label>Verification Code</label>
                                    <input type="text" inputMode="numeric" maxLength={6} placeholder="6-digit code" value={secForm.otpCode || ''} onChange={e => setSecForm(p => ({ ...p, otpCode: e.target.value.replace(/\D/g, '').slice(0, 6) }))} style={{ letterSpacing: 8, textAlign: 'center', fontSize: 20, fontWeight: 600 }} />
                                </div>
                                <button className="mpv-sec-modal-submit danger" disabled={secLoading} onClick={handleDisable2FA}>{secLoading ? 'Unbinding...' : 'Disable Authenticator'}</button>
                            </>
                        )}
                        {secModal === 'change-2fa' && (
                            <>
                                <h3 className="mpv-sec-modal-title"><RefreshCw size={18} /> Change 2FA</h3>
                                {authStep === 1 && (
                                    <div className="mpv-auth-step">
                                        <p className="mpv-sec-modal-hint">Step 1: Enter your current Google Authenticator code.</p>
                                        <div className="mpv-sec-modal-field">
                                            <label>Current Code</label>
                                            <input type="text" inputMode="numeric" maxLength={6} placeholder="6-digit code" value={secForm.oldCode || ''} onChange={e => setSecForm(p => ({ ...p, oldCode: e.target.value.replace(/\D/g, '').slice(0, 6) }))} style={{ letterSpacing: 8, textAlign: 'center', fontSize: 20, fontWeight: 600 }} />
                                        </div>
                                        <button className="mpv-sec-modal-submit" disabled={!secForm.oldCode || secForm.oldCode.length !== 6} onClick={() => setAuthStep(2)}>Next →</button>
                                    </div>
                                )}
                                {authStep === 2 && (
                                    <div className="mpv-auth-step">
                                        <p className="mpv-sec-modal-hint">Step 2: Scan the new QR code or enter the new secret key manually.</p>
                                        <div className="mpv-auth-qr">
                                            <img src={`https://api.qrserver.com/v1/create-qr-code/?size=180x180&data=otpauth://totp/Bicoin:${encodeURIComponent(userEmail)}?secret=${authSecret}%26issuer=Bicoin`} alt="QR Code" style={{ width: 180, height: 180, borderRadius: 8, background: '#fff', padding: 8 }} />
                                        </div>
                                        <div className="mpv-auth-secret">
                                            <span className="mpv-auth-secret-label">New Secret Key</span>
                                            <div className="mpv-auth-secret-box"><code>{authSecret}</code><Copy size={14} style={{ cursor: 'pointer', flexShrink: 0 }} onClick={() => copyToClipboard(authSecret)} /></div>
                                        </div>
                                        <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
                                            <button className="mpv-sec-modal-submit" style={{ background: 'var(--ui-bg-elevated)', flex: 1 }} onClick={() => setAuthStep(1)}>← Back</button>
                                            <button className="mpv-sec-modal-submit" style={{ flex: 1 }} onClick={() => setAuthStep(3)}>Next →</button>
                                        </div>
                                    </div>
                                )}
                                {authStep === 3 && (
                                    <div className="mpv-auth-step">
                                        <p className="mpv-sec-modal-hint">Step 3: Enter the new 6-digit code from Google Authenticator.</p>
                                        <div className="mpv-sec-modal-field">
                                            <label>New Verification Code</label>
                                            <input type="text" inputMode="numeric" maxLength={6} placeholder="6-digit code" value={secForm.otpCode || ''} onChange={e => setSecForm(p => ({ ...p, otpCode: e.target.value.replace(/\D/g, '').slice(0, 6) }))} style={{ letterSpacing: 8, textAlign: 'center', fontSize: 20, fontWeight: 600 }} />
                                        </div>
                                        <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
                                            <button className="mpv-sec-modal-submit" style={{ background: 'var(--ui-bg-elevated)', flex: 1 }} onClick={() => setAuthStep(2)}>← Back</button>
                                            <button className="mpv-sec-modal-submit" style={{ flex: 1 }} disabled={secLoading} onClick={handleChange2FA}>{secLoading ? 'Verifying...' : 'Change Authenticator'}</button>
                                        </div>
                                    </div>
                                )}
                            </>
                        )}
                        {secModal === 'fund-password' && (
                            <>
                                <h3 className="mpv-sec-modal-title"><ShieldCheck size={18} /> {profile?.hasFundPassword ? 'Change' : 'Set'} Fund Password</h3>
                                <p className="mpv-sec-modal-hint">Login password verification is required</p>
                                <div className="mpv-sec-modal-field"><label>Login Password</label><input type="password" placeholder="Enter login password" value={secForm.currentPassword || ''} onChange={e => setSecForm(p => ({ ...p, currentPassword: e.target.value }))} /></div>
                                <div className="mpv-sec-modal-field"><label>New Fund Password</label><input type="password" placeholder="Enter fund password" value={secForm.newFundPassword || ''} onChange={e => setSecForm(p => ({ ...p, newFundPassword: e.target.value }))} /></div>
                                <div className="mpv-sec-modal-field"><label>Confirm Fund Password</label><input type="password" placeholder="Confirm fund password" value={secForm.confirmFundPassword || ''} onChange={e => setSecForm(p => ({ ...p, confirmFundPassword: e.target.value }))} /></div>
                                <button className="mpv-sec-modal-submit" disabled={secLoading} onClick={handleSetFundPassword}>{secLoading ? 'Saving...' : (profile?.hasFundPassword ? 'Change Fund Password' : 'Set Fund Password')}</button>
                            </>
                        )}
                        {secError && <p className="mpv-sec-modal-error">{secError}</p>}
                        {secSuccess && <p className="mpv-sec-modal-success">{secSuccess}</p>}
                    </div>
                </div>
            )}

            {/* Nickname Edit Modal */}
            {showNicknameModal && (
                <div className="mpv-sec-modal-overlay" onClick={() => setShowNicknameModal(false)}>
                    <div className="mpv-sec-modal" onClick={e => e.stopPropagation()} style={{ maxWidth: 400 }}>
                        <button className="mpv-sec-modal-close" onClick={() => setShowNicknameModal(false)}><X size={18} /></button>
                        <h3 className="mpv-sec-modal-title" style={{ marginBottom: 8 }}>Edit Nickname</h3>
                        <p style={{ color: 'var(--ui-text-muted)', fontSize: 13, marginBottom: 16 }}>Set a customized nickname for your account</p>
                        <div style={{ marginBottom: 16 }}>
                            <label style={{ color: 'var(--ui-text-secondary)', fontSize: 12, display: 'block', marginBottom: 6 }}>Nickname</label>
                            <input
                                type="text"
                                value={nicknameInput}
                                onChange={e => setNicknameInput(e.target.value)}
                                placeholder="Enter your nickname"
                                maxLength={50}
                                style={{ width: '100%', padding: '10px 14px', background: 'var(--ui-bg-elevated)', border: '1px solid var(--ui-border)', borderRadius: 8, color: 'var(--ui-text-primary)', fontSize: 14, outline: 'none' }}
                                onKeyDown={e => { if (e.key === 'Enter') handleNicknameSave() }}
                                autoFocus
                            />
                        </div>
                        {nicknameSuccess && <p style={{ color: nicknameSuccess.includes('Failed') ? '#ef4444' : '#1CD4A7', fontSize: 13, marginBottom: 12 }}>{nicknameSuccess}</p>}
                        <div style={{ display: 'flex', gap: 10 }}>
                            <button onClick={() => setShowNicknameModal(false)} style={{ flex: 1, padding: '10px', background: 'var(--ui-bg-elevated)', border: '1px solid var(--ui-border)', borderRadius: 8, color: 'var(--ui-text-primary)', cursor: 'pointer', fontSize: 14 }}>Cancel</button>
                            <button onClick={handleNicknameSave} disabled={nicknameSaving || !nicknameInput.trim()} style={{ flex: 1, padding: '10px', background: '#1CD4A7', border: 'none', borderRadius: 8, color: '#0f0f14', cursor: 'pointer', fontSize: 14, fontWeight: 600, opacity: nicknameSaving || !nicknameInput.trim() ? 0.5 : 1 }}>
                                {nicknameSaving ? 'Saving...' : 'Save'}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Order Confirmation Modal */}
            {showOrderConfirmModal && (
                <div className="mpv-sec-modal-overlay" onClick={() => setShowOrderConfirmModal(false)}>
                    <div className="mpv-sec-modal" onClick={e => e.stopPropagation()} style={{ maxWidth: 400 }}>
                        <button className="mpv-sec-modal-close" onClick={() => setShowOrderConfirmModal(false)}><X size={18} /></button>
                        <h3 className="mpv-sec-modal-title" style={{ marginBottom: 8 }}>Order Confirmation</h3>
                        <p style={{ color: 'var(--ui-text-muted)', fontSize: 13, marginBottom: 20 }}>Enable reminders for order types that need to be reconfirmed every time</p>
                        {[
                            { key: 'limit', label: 'Limit order', desc: 'Confirm before placing limit orders' },
                            { key: 'market', label: 'Market order', desc: 'Confirm before placing market orders' },
                            { key: 'stop', label: 'Stop limit', desc: 'Confirm before placing stop-limit orders' },
                        ].map(item => (
                            <div key={item.key} onClick={() => handleOrderConfirmToggle(item.key)} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '14px 0', borderBottom: '1px solid #1a1a24', cursor: 'pointer' }}>
                                <div>
                                    <div style={{ color: 'var(--ui-text-primary)', fontSize: 14, fontWeight: 500 }}>{item.label}</div>
                                    <div style={{ color: 'var(--ui-text-muted)', fontSize: 12, marginTop: 2 }}>{item.desc}</div>
                                </div>
                                <div style={{ width: 44, height: 24, borderRadius: 12, background: orderConfirmSettings[item.key] ? '#1CD4A7' : '#2a2a3a', padding: 2, transition: 'background 0.2s', flexShrink: 0 }}>
                                    <div style={{ width: 20, height: 20, borderRadius: '50%', background: '#fff', transform: orderConfirmSettings[item.key] ? 'translateX(20px)' : 'translateX(0)', transition: 'transform 0.2s' }} />
                                </div>
                            </div>
                        ))}
                        <button onClick={() => setShowOrderConfirmModal(false)} style={{ width: '100%', marginTop: 20, padding: '10px', background: '#1CD4A7', border: 'none', borderRadius: 8, color: '#0f0f14', cursor: 'pointer', fontSize: 14, fontWeight: 600 }}>Done</button>
                    </div>
                </div>
            )}

            {/* Invitation Code Modal */}
            {showInviteCodeModal && (
                <div className="mpv-sec-modal-overlay" onClick={() => setShowInviteCodeModal(false)}>
                    <div className="mpv-sec-modal" onClick={e => e.stopPropagation()} style={{ maxWidth: 400 }}>
                        <button className="mpv-sec-modal-close" onClick={() => setShowInviteCodeModal(false)}><X size={18} /></button>
                        <h3 className="mpv-sec-modal-title" style={{ marginBottom: 8 }}>Add Superior's Invitation Code</h3>
                        <p style={{ color: 'var(--ui-text-muted)', fontSize: 13, marginBottom: 16 }}>Enter the invitation code from your referrer to establish the invitation relationship</p>
                        <div style={{ marginBottom: 16 }}>
                            <label style={{ color: 'var(--ui-text-secondary)', fontSize: 12, display: 'block', marginBottom: 6 }}>Invitation Code</label>
                            <input
                                type="text"
                                value={inviteCodeInput}
                                onChange={e => setInviteCodeInput(e.target.value.toUpperCase())}
                                placeholder="Enter invitation code"
                                maxLength={20}
                                style={{ width: '100%', padding: '10px 14px', background: 'var(--ui-bg-elevated)', border: '1px solid var(--ui-border)', borderRadius: 8, color: 'var(--ui-text-primary)', fontSize: 14, outline: 'none', letterSpacing: 1 }}
                                onKeyDown={e => { if (e.key === 'Enter') handleInviteCodeSave() }}
                                autoFocus
                            />
                        </div>
                        {savedInviteCode && !inviteCodeSuccess && (
                            <p style={{ color: 'var(--ui-text-muted)', fontSize: 12, marginBottom: 12 }}>Current code: <span style={{ color: '#1CD4A7' }}>{savedInviteCode}</span></p>
                        )}
                        {inviteCodeSuccess && <p style={{ color: inviteCodeSuccess.includes('Failed') ? '#ef4444' : '#1CD4A7', fontSize: 13, marginBottom: 12 }}>{inviteCodeSuccess}</p>}
                        <div style={{ display: 'flex', gap: 10 }}>
                            <button onClick={() => setShowInviteCodeModal(false)} style={{ flex: 1, padding: '10px', background: 'var(--ui-bg-elevated)', border: '1px solid var(--ui-border)', borderRadius: 8, color: 'var(--ui-text-primary)', cursor: 'pointer', fontSize: 14 }}>Cancel</button>
                            <button onClick={handleInviteCodeSave} disabled={inviteCodeSaving || !inviteCodeInput.trim()} style={{ flex: 1, padding: '10px', background: '#1CD4A7', border: 'none', borderRadius: 8, color: '#0f0f14', cursor: 'pointer', fontSize: 14, fontWeight: 600, opacity: inviteCodeSaving || !inviteCodeInput.trim() ? 0.5 : 1 }}>
                                {inviteCodeSaving ? 'Saving...' : 'Save'}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </Layout>
    )
}

